import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Button, Form, Modal, Segmented, Tag, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { RootState } from '@/redux';
import { posService } from '@/services/pos.service';
import { shippingService } from '@/services/shipping.service';
import { getApiError } from '@/utils/apiError';
import { findMissingDiscountReasons, isDiscountPolicyChannel } from './utils/discountPolicy';
import { clampTradeDiscount } from './utils/clampTradeDiscount';
import { formatMissingReasonError } from './utils/discountReasonError';
import { formatVND } from '@/utils/format';
import { getPosShiftStoreId, usePosShiftStoreId } from '@/utils/posShift';
import { useGeneralConfig } from '@/hooks/useGeneralConfig';
import { useServiceList, SERVICE_LIST_MAX_PAGE_SIZE } from '@/hooks/useServiceList';
import { usePosServiceEffectiveVat } from '@/hooks/usePosServiceEffectiveVat';
import { useRbac } from '@/hooks/useRbac';
import { computePosMoney, type ServiceTenantType } from '@tcr-erp/shared';
import { CareStaffAsyncSelect } from '@/components/pos/CareStaffAsyncSelect';
import { CustomerSidebar } from './components/CustomerSidebar';
import type { CustomerSidebarHandle } from './components/CustomerSidebar';
import { ComboSuggestBanner } from './components/ComboSuggestBanner';
import { ComboBlockedHint } from './components/ComboBlockedHint';
import { useComboSuggestions } from './hooks/useComboSuggestions';
import {
  applyComboSuggestion,
  splitComboGroup,
  removeComboGroup,
  setComboGroupCount,
  maxComboCount,
} from './utils/applyCombo';
import { mergeLooseDuplicates } from './utils/mergeLooseDuplicates';
import { splitItemLine } from './utils/splitLine';
import { checkoutLockMessage } from './utils/checkoutLockMessage';
import {
  addComboDeficitLines,
  maxComboCountByStock,
  planComboGrowth,
} from './utils/growComboGroup';
import type { PosComboDefinition } from './utils/comboMatcher';
import {
  customerValidationPolicyProblem,
  customerValidationRequirements,
  deriveDeliverySnapshot,
  type CustomerCheckoutValues,
  type CustomerValidationPolicy,
} from './utils/customerCheckout';
import { QRSearchBar } from './components/QRSearchBar';
import { BulkQrModalButton } from './components/BulkQrModalButton';
import { QuickAddItemsModal } from './components/QuickAddItemsModal';
import {
  appendComboPicksToCart,
  mergeQuickAddIntoCart,
  type QuickAddComboPick,
  type QuickAddEntry,
} from './utils/quickAddItems';
import { addComboToCart } from './utils/addComboToCart';
import { OrderItemsTable } from './components/OrderItemsTable';
import { ProductSuggestPanel } from './components/ProductSuggestPanel';
import {
  recalcItem,
  applySwappedVariant,
  computePosTotals,
  variantToOrderItem,
  rebalanceFlashGroup,
} from './utils/orderItemCalc';
import {
  applyB2BSuggestToOrderItems,
  clearB2BSuggestFields,
} from './utils/suggestPosB2BPrices';
import {
  sumVoucherDiscount,
  partitionValidatedVouchers,
  computeTotalAfterVoucher,
} from './utils/voucherTotals';
import {
  pruneGiftItemsForCodes,
  pruneVouchersForRemovedGift,
  isVoucherGiftItem,
} from './utils/giftItems';
import { PromotionGiftBanner, type PromotionFreeItem } from './components/PromotionGiftBanner';
import { GiftVariantPickerDialog } from './components/GiftVariantPickerDialog';
import { usePromotionGiftAdd } from './hooks/usePromotionGiftAdd';
import { usePosValidateVoucher } from '@/hooks/usePromotions';
import type { VoucherItem } from './components/CustomerSidebar/VoucherModal';
import { FloatingField } from './components/CustomerSidebar/FloatingField';
import { mapDetailToPosVariant } from './utils/mapVariantDetail';
import { validatePosItems, type PosItemError } from './utils/validatePosItems';
import { scrollToVisibleItemRow } from './utils/scrollToItemRow';
import { type FormErrorField } from './utils/buildCheckoutProblems';
import { runCheckoutGates } from './utils/runCheckoutGates';
import { buildCreateOrderPayload } from './utils/buildCreateOrderPayload';
import { buildEvaluateItems } from './utils/buildEvaluateItems';
import {
  parseFlashSaleSoldOut,
  proveOrderNotCreated,
  resolveCheckoutErrorMessage,
} from './utils/checkoutErrors';
import {
  requiresCustomerDialog,
} from './components/CustomerSidebar/customerFormSchema';
import {
  handleFulfillmentModeChange,
  type FulfillmentChangeConfirmConfig,
} from './utils/fulfillmentChange';
import { PaymentFooter } from './components/PaymentFooter';
import { CustomerNoteField } from './components/CustomerNoteField';
import { OrderInfoPanel } from './components/OrderInfoPanel';
import type { ServiceOption, ServiceFeeValue } from './components/ServiceFeeRow';
import { PaymentMethodFields } from './components/PaymentMethodFields';
import { ShippingTotals } from './components/Fulfillment/ShippingTotals';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { customerService } from '@/services/customer.service';
import { buildPosMoneyInput } from './utils/buildPosMoneyInput';
import { dropFlashPrice } from './utils/dropFlashPrice';
import { shouldResetDebt, type DebtResetInputs } from './utils/debtReset';
import { useShippingCarriers } from './hooks/useShippingCarriers';
import { useCarrierFeeEstimates } from './hooks/useCarrierFeeEstimates';
import { usePickupLocations } from '@/hooks/useShipping';
import type { PickupLocation } from '@/services/shipping.service';
import type { EstimatableCarrierCode } from '@/services/shipping.service';
import type { ShippingOrderLabel } from '@/constants/shippingCarriers';
import { isEstimatableCarrierCode } from '@/constants/shippingCarriers';
import { CarrierPanel, type CarrierPanelValue } from './components/Fulfillment/CarrierPanel';
import { normalizeOrderLabel } from './components/Fulfillment/orderLabelOptions';
import { PackageInfoFields, DEFAULT_PACKAGE_INFO } from './components/Fulfillment/PackageInfoFields';
import { FulfillmentFormSection } from './components/Fulfillment/FulfillmentFormSection';
import {
  effectiveShippingMode,
  type FulfillmentChoice,
  type ShippingModeValue,
} from './components/Fulfillment/fulfillmentChoice';
import { buildShippingCodLimitError } from '@tcr-erp/shared';
import type { ShippingMode, ShippingFeePayer } from './utils/shippingTotals';
import { buildShippingPayload } from './utils/buildShippingPayload';
import { NfcScanOverlay } from './components/NfcScanOverlay';
import { CheckoutSuccessDialog } from './components/CheckoutSuccessDialog';
import type { CheckoutSuccessResult } from './utils/buildReceiptData';
import { useMissingCustomerFields } from './hooks/useCustomerReady';
import { usePosTabs, MAX_TABS } from './hooks/usePosTabs';
import { usePosTabsHotkeys } from './hooks/usePosTabsHotkeys';
// TCR-2429 — đơn B2B: ô "Nhân viên chăm sóc" đổi nghĩa thành NVKD ghi nhận doanh số.
import {
  careStaffFieldCopy,
  careStaffRules,
  defaultCareStaffIdForMode,
} from './utils/careStaffField';
import { POS_STAFF_PAGE_LIMIT, posStaffQueryKey } from './utils/posStaffQuery';
import { useCareStaffModeSync } from './hooks/useCareStaffModeSync';
import { isB2BSalesUser } from '@/utils/salesManagerRoles';
import { PosTabsBar } from './components/PosTabsBar';
import { usePosFullscreen } from './PosFullscreenContext';
import { readPosCustomerPrefill } from './utils/customerPrefill';
import { UserType } from './types';
import type {
  OrderItem,
  OrderType,
  FulfillmentMode,
  PaymentMethod,
  PosComboSearchItem,
  PosOrderPrefill,
  PosVariant,
  PosVariantDetailItem,
  CommissionStaff,
  PosCheckoutAttempt,
  PosPromotionReconciliationStatus,
} from './types';
import './POSPage.css';

/**
 * Cờ bật/tắt bắt buộc mở ca khi vào POS (EG-369).
 * TẠM TẮT: module "Mở ca làm việc" đang lỗi → cho vào POS trực tiếp không bị ép
 * redirect sang /pos/shifts. Đổi về `true` để bật lại enforce khi module đã fix.
 */
const ENFORCE_POS_SHIFT = false;

/** Fallback khi `useGeneralConfig` chưa load/lỗi — an toàn giống mặc định nghiệp vụ. */
const DEFAULT_SALES_MODES: FulfillmentMode[] = ['INSTANT'];

function promotionReconciliationGuidance(
  status: Exclude<PosPromotionReconciliationStatus, 'completed'>,
  orderReference: string,
): string {
  const prefix = `Đơn #${orderReference} đã được ghi nhận. `;
  switch (status) {
    case 'pending':
      return `${prefix}Khuyến mãi đang được xử lý; số tiền thanh toán không thay đổi. Không áp lại voucher hoặc flash sale; nếu thử lại, hệ thống sẽ giữ nguyên mã xử lý của đơn này.`;
    case 'running':
      return `${prefix}Khuyến mãi đang được xử lý; số tiền thanh toán không thay đổi. Vui lòng không tạo lại đơn hoặc áp lại voucher, flash sale.`;
    case 'retry_scheduled':
      return `${prefix}Khuyến mãi sẽ được thử lại tự động; số tiền thanh toán không thay đổi. Không áp lại voucher hoặc flash sale.`;
    case 'cancellation_pending':
      return `${prefix}Đang huỷ khuyến mãi; số tiền thanh toán không thay đổi. Vui lòng chờ hệ thống hoàn tất đối soát.`;
    case 'canceled':
      return `${prefix}Khuyến mãi đã được huỷ; số tiền thanh toán không thay đổi. Không tạo lại đơn này.`;
    case 'manual_reconciliation':
      return `${prefix}Khuyến mãi cần được đối soát thủ công; số tiền thanh toán không thay đổi. Không áp lại voucher hoặc flash sale; vui lòng liên hệ quản trị viên để xử lý.`;
  }
}

/**
 * TCR-1096 — đọc payload "Sao chép đơn hàng" từ `location.state`, CÓ kiểm hình dạng.
 *
 * Nguồn của state là history entry của trình duyệt: nó sống qua deploy, qua
 * F5, và người dùng có thể quay lại một entry được tạo bởi bản build cũ. Tin
 * vào cast rồi gọi array method lên `orderItems` là cách làm POS trắng màn cho
 * tới khi ai đó xoá history hộ thu ngân. Không khớp ⇒ trả `undefined` (no-op),
 * KHÔNG throw.
 */
function readPosOrderPrefill(state: unknown): PosOrderPrefill | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const candidate = (state as { copyFromOrder?: unknown }).copyFromOrder;
  if (typeof candidate !== 'object' || candidate === null) return undefined;
  const { orderItems, customerForm } = candidate as {
    orderItems?: unknown;
    customerForm?: unknown;
  };
  if (!Array.isArray(orderItems)) return undefined;
  if (typeof customerForm !== 'object' || customerForm === null) return undefined;
  return candidate as PosOrderPrefill;
}

const NON_CUSTOMER_FORM_KEYS = new Set([
  'care_staff_id',
  'delivery_same_as_customer',
  'invoice_same_as_delivery',
  'receive_method',
  'user_type',
]);

function hasUsefulCustomerForm(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  return Object.entries(value).some(
    ([key, fieldValue]) =>
      !NON_CUSTOMER_FORM_KEYS.has(key) && fieldValue !== undefined && fieldValue !== null && fieldValue !== '',
  );
}

function hasOwnCustomerPrefill(state: unknown): state is Record<string, unknown> {
  return (
    typeof state === 'object' &&
    state !== null &&
    Object.prototype.hasOwnProperty.call(state, 'customerPrefill')
  );
}

function removeCustomerPrefill(state: unknown): unknown {
  if (typeof state !== 'object' || state === null) return null;
  const nextState = { ...(state as Record<string, unknown>) };
  delete nextState.customerPrefill;
  return Object.keys(nextState).length > 0 ? nextState : null;
}

/**
 * Chữ ký của giỏ hàng, để biết giỏ có đổi giữa lúc validate và lúc gọi API tạo
 * đơn hay không.
 *
 * So NỘI DUNG chứ không so tham chiếu mảng: `setOrderItems` chạy qua
 * `prev.map(...)` ở nhiều đường (làm mới giá vàng, gợi ý giá B2B, reset kho khi
 * đổi chi nhánh) nên hoàn toàn có thể sinh mảng MỚI với nội dung Y HỆT — so
 * identity sẽ chặn oan một đơn không có gì thay đổi, và thu ngân không có cách
 * nào hiểu vì sao. Ngược lại, thứ tự dòng đổi mà nội dung giữ nguyên thì chữ ký
 * cũng đổi, và chặn là đúng: payload gửi theo snapshot cũ.
 *
 * Liệt kê đúng các field ảnh hưởng tới payload/tiền hoặc tới `validatePosItems`.
 * THÊM field mới vào payload thì phải thêm vào đây, nếu không guard sẽ mù với
 * đúng field đó.
 */
export function cartSignature(items: OrderItem[]): string {
  return items
    .map((i) =>
      [
        i.id,
        i.variantId,
        i.variantCode,
        i.specificationCode ?? '',
        i.quantity,
        i.price,
        i.discountPercent,
        i.discountAmount,
        i.totalAmount,
        i.vat ?? '',
        i.vatOverride ?? '',
        i.sellerStaffId ?? '',
        i.warehouseId ?? '',
        // Final review Fix 5 — `override_reason` đi vào payload tạo đơn
        // (`buildCreateOrderPayload.ts`) từ Task 3 nhưng guard này chưa từng
        // được cập nhật theo: sửa/xoá lý do CK GIỮA lúc validate và lúc gọi
        // API tạo đơn trước đây trôi qua guard vô hình (chữ ký không đổi) dù
        // payload thật sự đã khác — đúng lỗ hổng mà comment ngay trên hàm này
        // cảnh báo.
        i.overrideReason ?? '',
        // Combo ảo: cả ba field này ĐỀU đi vào payload `POST /pos/orders`
        // (`buildCreateOrderPayload`) — `combo_group_id`, `combo_id` và
        // `combo_unit_price` (assertion giá đã hiển thị, I1). Thiếu chúng ở đây
        // thì guard mù đúng với thao tác nguy hiểm nhất: thu ngân bấm Áp
        // dụng/Tách combo GIỮA lúc request tạo đơn đang bay — số lượng và giá
        // dòng có thể không đổi (tách một cụm 1 combo về đúng giá lẻ cũ), nhưng
        // đơn vẫn lên BE với `combo_group_id` của một cụm không còn tồn tại.
        i.comboGroupId ?? '',
        i.comboId ?? '',
        i.comboUnitPrice ?? '',
        // Không vào payload nhưng quyết định TỔNG hiển thị của cụm — và không
        // thể đổi mà không đổi quantity, nên thêm vào đây không sinh chặn oan.
        i.comboUnitCount ?? '',
      ].join(':'),
    )
    .join('|');
}

function checkoutInputSignature(input: {
  formValues: Record<string, unknown>;
  cartSignature: string;
  fulfillmentMode: FulfillmentMode;
  orderType: OrderType;
  paymentMethod: PaymentMethod;
  paymentAccountId?: string;
  nonCashAmount?: number;
  cashReceived?: number;
  selectedSessionId?: string;
  commissionStaff: CommissionStaff[];
  // TCR-1963 F1 (final review fix) — "Lưu đơn nháp" và "Ghi hoá đơn và thanh
  // toán" là hai Ý ĐỊNH khác hẳn nhau dù mọi input khác (giỏ/form/tiền) giống
  // hệt. Thiếu field này, bấm nút này rồi bấm nút kia trong lúc attempt trước
  // còn sống (đối soát khuyến mãi pending, chưa rõ kết quả...) không bị coi là
  // "input đổi" — FE gửi lại request thứ hai BẰNG CHÍNH idempotency key cũ chỉ
  // khác `save_as_draft`, để lộ ra tận lỗi 409 xấu của BE (xem F1 be-tenant:
  // `assertIdempotencyRequestMatches`) thay vì bị khoá tab với thông báo quen
  // thuộc `checkoutLockMessage`.
  saveAsDraft?: boolean;
}): string {
  try {
    return JSON.stringify(input) ?? '';
  } catch {
    return '';
  }
}

function sameCheckoutMoney(
  left: PosCheckoutAttempt['money'],
  right: PosCheckoutAttempt['money'],
): boolean {
  return (
    left.customerPayable === right.customerPayable &&
    left.collectTotal === right.collectTotal &&
    left.cod === right.cod &&
    left.debt === right.debt &&
    left.orderRevenue === right.orderRevenue &&
    left.amountCollectedNow === right.amountCollectedNow &&
    left.nonCashAmount === right.nonCashAmount &&
    left.paymentMethod === right.paymentMethod
  );
}

/**
 * Toast "chưa ghi được hoá đơn" — MỘT chỗ dựng cho mọi cổng chặn.
 *
 * Trước đây cụm JSX này bị dán hai lần (cổng thông tin khách và cổng ĐVVC): sửa
 * nhãn ở một chỗ mà quên chỗ kia là hai kiểu thông báo cho cùng một hành động.
 */
function showCheckoutProblems(problems: string[]): void {
  void message.error({
    content: (
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          Chưa ghi được hoá đơn — vui lòng kiểm tra:
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>
    ),
    duration: 6,
  });
}

/**
 * Task 18: KHÔNG còn nguồn số cho màn hình/checkout — `computePosMoney`
 * (`buildPosMoneyInput`, gọi trong `useMemo` ở thân component) mới là nguồn đó.
 *
 * Hàm này CHỈ còn một việc: mốc SO SÁNH rẻ cho effect reset công nợ (dưới) —
 * phát hiện "bốn nguồn quyết định COD mặc định vừa đổi" để biết có nên xoá số
 * nợ thu ngân đã gõ hay không. Không cần chính xác tuyệt đối như
 * `computePosMoney` (không tính voucher/loyalty theo đúng thứ tự phân bổ VAT),
 * chỉ cần ĐỔI đúng lúc số tiền thật đổi — cùng input, cùng công thức cộng trừ
 * đơn giản là đủ cho việc phát hiện thay đổi.
 */
function computePosPayable(
  items: Parameters<typeof computePosTotals>[0],
  surcharge: number,
  voucherDiscount: number,
  tradeDiscount: number,
): number {
  return computeTotalAfterVoucher(
    computePosTotals(items).grandTotal + surcharge,
    voucherDiscount,
    tradeDiscount,
  );
}

export function POSPage() {
  const [form] = Form.useForm();
  const { hasPermission } = useRbac();
  // Dùng CHUNG với effect "enforce shift presence" phía dưới — tránh
  // subscribe `state.auth.user` hai lần trong cùng component. Khai báo lên
  // đây (thay vì giữ nguyên chỗ cũ, sau effect tab-switch) vì Task 7 cần
  // đọc `user?.id` trong effect tab-switch, nằm TRƯỚC vị trí khai báo cũ —
  // để nguyên chỗ cũ sẽ vỡ theo temporal-dead-zone (dùng biến `const` trước
  // dòng khai báo của chính nó).
  const user = useSelector((state: RootState) => state.auth.user);
  const { state: tabsState, active, addTab, closeTab, switchTab, patchActive, patchTab } =
    usePosTabs();
  const fulfillmentMode = active.fulfillmentMode;
  const generalConfigQuery = useGeneralConfig();
  const customerValidationPolicy: CustomerValidationPolicy = generalConfigQuery.isError
    ? { scope: 'pos', status: 'error' }
    : generalConfigQuery.isSuccess
      ? {
          scope: 'pos',
          status: 'ready',
          personalCccdRequired:
            generalConfigQuery.data.pos_personal_cccd_required,
        }
      : { scope: 'pos', status: 'loading' };
  const validationRequirements = customerValidationRequirements(customerValidationPolicy);
  const validationPolicyProblem = customerValidationPolicyProblem(customerValidationPolicy);
  // antd Form is uncontrolled: cả gõ tay lẫn `setFieldsValue` (chọn/xoá khách ở
  // sidebar) đều không re-render POSPage. Hook watch thẳng form store nên danh
  // sách trường còn thiếu luôn cập nhật theo cả hai đường ghi — dùng lúc bấm
  // "Ghi hoá đơn và thanh toán" để dựng toast lỗi.
  const missingCustomerInfo = useMissingCustomerFields(
    form,
    fulfillmentMode,
    customerValidationPolicy,
  );
  const sidebarRef = useRef<CustomerSidebarHandle | null>(null);
  const serialTrackingEnabled = generalConfigQuery.data?.serial_tracking_enabled ?? false;
  const posWeightColumnEnabled = generalConfigQuery.data?.pos_weight_column_enabled ?? false;
  const allowedSalesModes = useMemo(
    () => generalConfigQuery.data?.pos_sales_modes ?? DEFAULT_SALES_MODES,
    [generalConfigQuery.data?.pos_sales_modes],
  );
  // Finding 2b (review cuối branch): suy theo MEMBERSHIP thay vì `.length` —
  // mảng có thể chứa phần tử trùng lặp (dữ liệu cũ trước khi BE thêm
  // `@ArrayUnique`, xem update-general.dto.ts) nên length không phản ánh đúng
  // SỐ MODE PHÂN BIỆT thực sự được phép.
  const canSellInstant = allowedSalesModes.includes('INSTANT');
  const canSellPreorder = allowedSalesModes.includes('PREORDER');

  // Task 18: bốn field cấu hình khối tiền dịch vụ (Settings → POS) — danh mục
  // dịch vụ được phép chọn cho Phụ thu/Phí vận chuyển tự vận chuyển, và thuế
  // suất mặc định khi thu ngân gõ tự do (không chọn dịch vụ nào).
  const defaultFulfillmentChoice: FulfillmentChoice =
    (generalConfigQuery.data?.pos_default_fulfillment_choice as FulfillmentChoice | undefined) ?? 'carrier';
  const surchargeServiceIds = generalConfigQuery.data?.pos_surcharge_service_ids ?? [];
  const shippingServiceIdsConfig = generalConfigQuery.data?.pos_shipping_service_ids ?? [];
  const surchargeDefaultVatRate = generalConfigQuery.data?.pos_surcharge_default_vat_rate ?? 0;
  const shippingDefaultVatRate = generalConfigQuery.data?.pos_shipping_default_vat_rate ?? 0;
  // Một trang là đủ cho dropdown: phụ thu/phí ship là vài dịch vụ khai riêng,
  // không phải catalog sản phẩm. KHÔNG được vượt `SERVICE_LIST_MAX_PAGE_SIZE`
  // (trần `@Max(100)` của BE) — vượt là 400, và lỗi đó bị nuốt thành danh sách
  // rỗng. Hook có kẹp sẵn, nhưng khai đúng ở đây để đọc code không hiểu nhầm.
  const serviceListQuery = useServiceList({ page_size: SERVICE_LIST_MAX_PAGE_SIZE });
  // TCR-1362 follow-up — VAT đã resolve qua nhóm ngành thuế cho dịch vụ không
  // tự khai vat_rate riêng. KHÔNG gộp vào `serviceOptionsLoading` bên dưới:
  // để picker không phải chờ thêm query này (thường trả về `{}`), degrade êm
  // về default tĩnh trong lúc tải như hành vi trước khi có fix này.
  const effectiveVatQuery = usePosServiceEffectiveVat();
  const serviceOptions = useMemo(() => {
    const services = serviceListQuery.data?.data ?? [];
    const toOption = (s: ServiceTenantType): ServiceOption => ({
      id: s._id,
      name: s.name,
      unitPrice: s.unit_price ?? null,
      vatRate: s.vat_rate ?? null,
      resolvedVatRate: effectiveVatQuery.data?.[s._id]?.vat_rate ?? null,
    });
    return {
      surcharge: services.filter((s) => surchargeServiceIds.includes(s._id)).map(toOption),
      shipping: services.filter((s) => shippingServiceIdsConfig.includes(s._id)).map(toOption),
    };
  }, [
    serviceListQuery.data,
    effectiveVatQuery.data,
    JSON.stringify(surchargeServiceIds),
    JSON.stringify(shippingServiceIdsConfig),
  ]);

  // TCR-1104: 1 trong 2 query trên (config Settings / danh sách dịch vụ) đang
  // tải LẦN ĐẦU (chưa có cache) ⇒ `serviceOptions` có thể rỗng/thiếu dù form
  // đã có sẵn `surcharge_service_id`/`shipping_service_id`. Truyền cờ này
  // xuống để Select hiện skeleton thay vì hiện trống đọc nhầm là mất lựa
  // chọn. `isLoading` (không phải `isFetching`) chỉ true ở lần tải đầu chưa
  // có cache — không lặp lại mỗi khi chuyển tab POS trong cùng phiên nhờ
  // `staleTime` 5 phút của `useGeneralConfig`.
  const serviceOptionsLoading = generalConfigQuery.isLoading || serviceListQuery.isLoading;

  const { posAppFullscreen } = usePosFullscreen();
  const [tabsPortalEl, setTabsPortalEl] = useState<HTMLElement | null>(null);

  // Tìm portal slot trong Header theo `posAppFullscreen` (suy từ pathname, nên
  // đã là `true` ngay từ lần render đầu tiên khi mount vào `/pos`).
  //
  // PHẢI là `useLayoutEffect`, KHÔNG phải `useEffect`: `tabsPortalEl` khởi tạo
  // `null`, nên ở lần render đầu — trước khi effect nào chạy — điều kiện
  // `posAppFullscreen && tabsPortalEl` bên dưới sai và `PosTabsBar` render
  // inline làm con đầu của form. `useEffect` chạy sau khi trình duyệt đã có thể
  // paint frame đó, rồi mới `setTabsPortalEl` gây thêm một commit đẩy thanh tab
  // nhảy chỗ — lộ khung hình trung gian bị đẩy layout ~40px. `useLayoutEffect`
  // chạy đồng bộ ngay sau commit, trước khi trình duyệt paint: nếu node portal
  // đã có mặt trong DOM tại thời điểm đó, `document.getElementById` tìm được
  // ngay trong cùng lần cập nhật, không lộ frame trung gian nào.
  useLayoutEffect(() => {
    setTabsPortalEl(posAppFullscreen ? document.getElementById('pos-tabs-portal-slot') : null);
  }, [posAppFullscreen]);

  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  });

  // Danh sách tab mới nhất, đồng bộ sau MỖI render. Dùng để đọc giỏ của một tab
  // KHÔNG active (ghi async đã chốt sẵn id tab đích) — tab đang active luôn đọc
  // qua `activeRef`. `setOrderItemsForTab` cũng tự cập nhật NGAY trong tick phần
  // tử của đúng tab vừa ghi vào đây (không chỉ vào `activeRef` khi tab đó đang
  // active): chưa có luồng sống nào ghi hai lần trong CÙNG một tick vào một tab
  // không active, nhưng nếu có thì snapshot ở đây vẫn đúng, không phải chờ effect
  // sau render mới thấy — cùng lớp bảo đảm với `activeRef`, phòng khi có luồng
  // mới trong tương lai rơi vào đúng hình dạng đó.
  const tabsRef = useRef(tabsState.tabs);
  useEffect(() => {
    tabsRef.current = tabsState.tabs;
  });

  const orderItems = active.orderItems;
  const voucherDiscount = (Form.useWatch('voucher_discount', form) as number | undefined) ?? 0;
  const promotionDiscount = (Form.useWatch('promotion_discount', form) as number | undefined) ?? 0;
  // Freeship — trục tiền RIÊNG, không cộng vào `promotionDiscount`:
  // `promotionDiscount` đi vào `orderDiscounts.voucher` (trừ vào tiền hàng, hạ
  // cơ sở tính thuế), còn khoản này trừ ở bước cộng phí ship.
  const shippingDiscount =
    (Form.useWatch('shipping_discount', form) as number | undefined) ?? 0;
  // TCR-1298 — freeship có thể đến từ KM tự động (`shippingDiscount > 0`, số
  // tiền THẬT) HOẶC voucher nhập tay loại free_shipping (`has_freeship_voucher`,
  // BE chưa tính được số tiền nên chỉ có cờ boolean — xem doc ở
  // `OrderInfoPanel.freeshipActive`). Cả hai đều phải khoá "Người trả phí vận
  // chuyển" về Người bán như nhau.
  const hasFreeshipVoucherApplied =
    (Form.useWatch('has_freeship_voucher', form) as boolean | undefined) ?? false;
  const freeshipActive = shippingDiscount > 0 || hasFreeshipVoucherApplied;
  const tradeDiscount = (Form.useWatch('trade_discount', form) as number | undefined) ?? 0;
  const surcharge = (Form.useWatch('surcharge_amount', form) as number | undefined) ?? 0;
  // Task 18: dịch vụ đã chọn cho dòng Phụ thu / dòng Phí vận chuyển (tự vận
  // chuyển) — mang theo thuế suất riêng nếu dịch vụ có khai (`ServiceFeeRow`).
  //
  // TCR-1104 root cause thật: 2 field này KHÔNG có `<Form.Item name=...>` nào
  // mount (Select trong `ServiceFeeRow` là controlled component thuần, ghi vào
  // form qua `setFieldsValue`, không qua Form.Item) — cùng lớp với
  // `loyalty_redeem_value` bên dưới. Thiếu `preserve: true`, `Form.useWatch`
  // không đáng tin cho field "ảo" kiểu này: đã đo bằng console.log trực tiếp —
  // `form.getFieldValue('surcharge_service_id')` đúng NGAY sau `setFieldsValue`,
  // nhưng `Form.useWatch('surcharge_service_id', form)` (không preserve) đứng
  // yên ở giá trị CŨ vô thời hạn. Hệ quả: `ServiceFeeRow` nhận `value.serviceId`
  // vẫn `null` trong khi `value.amount` đã đổi đúng (2 watch khác field, lệch
  // pha) → effect đồng bộ local ghi đè local.serviceId vừa chọn (đang đúng) về
  // `null` → Select hiện trống dù tiền/VAT đã tính đúng. Chọn LẦN 2 mới "ăn" vì
  // lúc đó field đã kịp có giá trị cũ khác `null` từ trước để so sánh lệch,
  // che giấu triệu chứng — không phải do 2 query async ở dưới (đó là bug khác,
  // đã fix riêng bằng `optionsLoading`/`serviceOptionsLoading`).
  const surchargeServiceId =
    (Form.useWatch('surcharge_service_id', { form, preserve: true }) as string | undefined) ?? null;
  const shippingServiceId =
    (Form.useWatch('shipping_service_id', { form, preserve: true }) as string | undefined) ?? null;
  // `loyalty_redeem_value` do `LoyaltyBadge` ghi vào một Form.Item ẩn nằm trong
  // CustomerSidebar. `preserve: true` là bắt buộc: field ẩn đó có thể chưa mount
  // (dialog khách hàng đóng) mà số tiền vẫn nằm trong store — thiếu nó thì COD
  // hiện ở cột phải lại quay về số CHƯA trừ điểm.
  const loyaltyRedeemValue =
    (Form.useWatch('loyalty_redeem_value', { form, preserve: true }) as number | undefined) ?? 0;
  const validateVoucher = usePosValidateVoucher();
  const orderType = active.orderType;
  const paymentMethod = active.paymentMethod;
  const selectedSessionId = active.selectedSessionId;
  const commissionStaff = active.commissionStaff;
  const cashReceived = active.cashReceived;
  const paymentAccountId = active.paymentAccountId;
  const nonCashAmount = active.nonCashAmount;

  /**
   * Ghi giỏ hàng vào MỘT tab ĐÍCH ĐÃ CHỐT ID — dùng cho mọi luồng ghi async.
   *
   * Tab được ĐỌC để dựng patch cũng phải là tab được GHI. `PATCH_ACTIVE`
   * ("tab nào đang active lúc reducer chạy") sai ở đây: patch đã tính sẵn từ
   * snapshot của một tab cụ thể, mà tab active có thể đã đổi giữa lúc đọc và
   * lúc dispatch được xử lý — `ADD_TAB` của "Sao chép đơn hàng" (TCR-1096) là
   * đúng ca đó, và effect dọn field B2B (`clearB2BSuggestFields`, chạy hai lần
   * dưới StrictMode) đắp giỏ RỖNG của tab cũ lên tab vừa sao chép, xoá sạch
   * hàng vừa mồi mà không một thông báo nào. Kịch bản thật không cần
   * StrictMode: `applyB2BSuggestToOrderItems` là async, thu ngân đổi tab trong
   * lúc chờ là giỏ tab mới bị ghi đè bằng giỏ tab cũ.
   *
   * `setOrderItems` bên dưới = biến thể "tab đang active NGAY LÚC GỌI", đúng
   * cho mọi lời gọi đồng bộ. Lời gọi nào có `await` xen giữa lúc đọc và lúc ghi
   * thì PHẢI tự chốt id (xem effect B2B suggest).
   */
  const setOrderItemsForTab = useCallback(
    (tabId: string, updater: OrderItem[] | ((prev: OrderItem[]) => OrderItem[])) => {
      const activeTab = activeRef.current;
      const isActive = activeTab.id === tabId;
      const current = isActive
        ? activeTab.orderItems
        : tabsRef.current.find((t) => t.id === tabId)?.orderItems;
      // Tab đã bị đóng trong lúc chờ ⇒ bỏ ghi (cùng quy ước với `PATCH_TAB`).
      if (current === undefined) return;
      const next = typeof updater === 'function' ? updater(current) : updater;
      // Patch RỖNG NGHĨA thì đừng dispatch. Nhiều updater (`clearB2BSuggestFields`,
      // `pruneGiftItemsForCodes`, ...) trả về ĐÚNG mảng cũ khi không có gì để
      // đổi. Chính một patch vô nghĩa như thế, dispatch qua `PATCH_ACTIVE` và
      // rơi nhầm sang tab khác, là toàn bộ bug TCR-1096. Chặn ở đây là hàng rào
      // rẻ nhất và phủ cả lớp bug đó, độc lập với việc id tab có đúng hay không.
      if (next === current) return;
      // Nhiều lời gọi trong CÙNG một tick (vd "Chọn kho cho cả cụm" gọi
      // `updateItem` lần lượt cho từng thành phần combo) đều đọc
      // `activeRef.current`/`tabsRef.current`, vốn chỉ được đồng bộ lại ở effect
      // SAU render. Không tự cập nhật ref ngay tại đây thì mọi lời gọi sau đều
      // dựng `next` từ snapshot cũ và lần dispatch cuối ghi đè sạch các lần
      // trước (chỉ dòng cuối cụm giữ được kho). Nhánh active cập nhật thẳng vào
      // `activeRef`; nhánh không active mirror y hệt vào `tabsRef` — cùng một lớp
      // bảo đảm, tab nào cũng không thể trôi khỏi state thật giữa hai lần ghi
      // liên tiếp trong một tick. Effect đồng bộ ref ở trên vẫn chạy sau mỗi
      // render như bình thường, không phụ thuộc vào việc này.
      if (isActive) {
        activeRef.current = { ...activeTab, orderItems: next };
      } else {
        tabsRef.current = tabsRef.current.map((t) => (t.id === tabId ? { ...t, orderItems: next } : t));
      }
      patchTab(tabId, { orderItems: next });
    },
    [patchTab],
  );

  const setOrderItems = useCallback(
    (updater: OrderItem[] | ((prev: OrderItem[]) => OrderItem[])) =>
      setOrderItemsForTab(activeRef.current.id, updater),
    [setOrderItemsForTab],
  );

  const userType = (Form.useWatch('user_type', form) as UserType | undefined) ?? UserType.PERSONAL;
  const customerUserId = Form.useWatch('customer_user_id', form) as string | undefined;
  const isB2B = userType === UserType.ORGANIZATION;
  // TCR-2429 — đơn B2B: ô `care_staff_id` là NVKD ghi nhận doanh số (BE: `care_staff_id || process_by`)
  // nên nhãn/placeholder/câu báo lỗi đổi theo chế độ, và danh sách lấy từ phạm vi NVKD toàn công ty.
  const careStaffCopy = careStaffFieldCopy(isB2B);
  const careStaffRequiredRules = careStaffRules(isB2B);
  const currentUserIsB2BSales = isB2BSalesUser(user);
  // TCR-2129 — kênh B2B của khách đang chọn (field ẩn do CustomerSidebar set).
  const formB2BChannel = Form.useWatch('_b2bChannel', form) as string | undefined;
  // Khách vào POS qua đường không đi qua dialog chọn khách (prefill từ CRM / sao chép đơn cũ)
  // có thể thiếu `_b2bChannel` ⇒ cột chính sách tắt, BE lại 400. Tra bù kênh theo id khách;
  // lỗi mạng → coi như không bật (không chặn bán hàng).
  const { data: fetchedCustomerForChannel } = useQuery({
    queryKey: ['pos-customer-b2b-channel', customerUserId],
    queryFn: () => customerService.getById(customerUserId as string),
    enabled: isB2B && !!customerUserId && !formB2BChannel,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const customerB2BChannel = formB2BChannel ?? fetchedCustomerForChannel?.b2b_channel ?? undefined;
  const discountPolicyEnabled = isB2B && !!customerUserId && isDiscountPolicyChannel(customerB2BChannel);
  const prevB2BCustomerRef = useRef(customerUserId);

  // Đổi KH (hoặc xoá KH) → bỏ sửa tay, lấy lại giá gợi ý theo KH mới.
  useEffect(() => {
    if (prevB2BCustomerRef.current === customerUserId) return;
    prevB2BCustomerRef.current = customerUserId;
    form.setFieldsValue({ trade_discount: 0, trade_discount_note: undefined });
    if (!isB2B) return;
    setOrderItems((prev) =>
      prev.some((item) => item.hasPriceOverride && !item.splitLine)
        ? // TCR-2614: dòng tách (hàng tặng giá 0) giữ cờ qua đổi khách.
          prev.map((item) => (item.splitLine ? item : { ...item, hasPriceOverride: false }))
        : prev,
    );
  }, [customerUserId, isB2B, setOrderItems, form]);

  /** Chỉ phụ thuộc SL/variant/KH — tránh loop khi cập nhật giá gợi ý. */
  const b2bSuggestKey = useMemo(() => {
    if (!isB2B) return '';
    return [
      customerUserId ?? '',
      ...orderItems.map((item) => `${item.id}:${item.variantId}:${item.quantity}:${item.brandId ?? ''}:${item.hasPriceOverride ? 1 : 0}`),
    ].join('|');
  }, [isB2B, customerUserId, orderItems]);

  useEffect(() => {
    if (!isB2B) {
      // TCR-1262: fulfillmentMode cần để clearB2BSuggestFields recalc lại
      // discountAmount/totalAmount khi nó reset item.price về giá bán lẻ.
      setOrderItems((prev) => clearB2BSuggestFields(prev, fulfillmentMode));
      form.setFieldsValue({ trade_discount: 0, trade_discount_note: undefined });
      return;
    }
    if (orderItems.length === 0) return;

    // CHỐT id tab NGAY LÚC NÀY, trước cái `await` bên dưới. `next` được tính từ
    // `orderItems` — snapshot của ĐÚNG tab này. Ghi theo "tab nào đang active
    // lúc promise resolve" là cùng một lớp bug với TCR-1096: thu ngân đổi tab
    // trong lúc chờ thì giỏ tab này bị đắp lên giỏ tab kia.
    //
    // Cờ `cancelled` thật ra CÓ che được ca này hôm nay: `b2bSuggestKey` mã hoá
    // `item.id` của từng dòng, mà mọi dòng — kể cả dòng "Sao chép đơn" dựng qua
    // `buildCopyOrderItems`/`variantToOrderItem` — đều mang một `uuidv4()` mới
    // riêng, nên hai tab không bao giờ trùng key thật và đổi tab luôn đổi key
    // ⇒ cleanup ⇒ `cancelled` chạy đúng. Chốt id tường minh ở đây không phải vá
    // một lỗ hổng đang xảy ra, mà vì code động tới tiền không nên dựa đúng vào
    // một cờ gián tiếp sống nhờ cách `b2bSuggestKey` được tính hôm nay — chốt id
    // tab là cơ chế trực tiếp, đúng bất kể sau này hàm đó đổi cách tính ra sao.
    const targetTabId = activeRef.current.id;
    let cancelled = false;
    void (async () => {
      // Đơn B2B ĐƯỢC giữ cụm combo (quyết định 2026-08-04): giá combo thắng
      // CKTM ở tầng giá dòng. `applyB2BSuggestToOrderItems` bỏ qua dòng thuộc
      // cụm, nên không còn lý do tách cụm khi đổi sang khách tổ chức.
      const next = await applyB2BSuggestToOrderItems(orderItems, fulfillmentMode, customerUserId, {
        trackPolicy: discountPolicyEnabled,
      });
      if (cancelled) return;
      if (next !== orderItems) setOrderItemsForTab(targetTabId, next);
    })();
    return () => {
      cancelled = true;
    };
    // b2bSuggestKey đã mã hoá orderItems + customer; fulfillmentMode cần khi recalc.
  }, [b2bSuggestKey, isB2B, fulfillmentMode, customerUserId, discountPolicyEnabled, setOrderItems, setOrderItemsForTab]);

  const setOrderType = useCallback((v: OrderType) => patchActive({ orderType: v }), [patchActive]);
  const setPaymentMethod = useCallback(
    // Reset account + split khi đổi method để NonCashPaymentSection auto-chọn
    // default của method mới (tránh gửi nhầm account id của method cũ).
    (v: PaymentMethod) =>
      patchActive({ paymentMethod: v, paymentAccountId: undefined, nonCashAmount: undefined }),
    [patchActive],
  );
  const setSelectedSessionId = useCallback(
    (v: string | undefined) => patchActive({ selectedSessionId: v }),
    [patchActive],
  );
  const setCommissionStaff = useCallback(
    (v: CommissionStaff[]) => patchActive({ commissionStaff: v }),
    [patchActive],
  );
  const setCashReceived = useCallback(
    (v: number) => patchActive({ cashReceived: v }),
    [patchActive],
  );
  const setPaymentAccountId = useCallback(
    (v: string | undefined) => patchActive({ paymentAccountId: v }),
    [patchActive],
  );
  const setNonCashAmount = useCallback(
    (v: number | undefined) => patchActive({ nonCashAmount: v }),
    [patchActive],
  );

  const shiftStoreId = usePosShiftStoreId();
  const selectedStoreId = shiftStoreId ?? undefined;

  // TCR-2429 (Codex finding 2) — danh sách NVKD dùng để XÁC THỰC `care_staff_id` khôi phục từ
  // snapshot của tab B2B. Cùng endpoint + cùng key cache với ô chọn (`StaffAsyncSelect`) nên
  // KHÔNG sinh request thứ hai: ô chọn trong đơn B2B cũng mở đúng query này.
  const {
    data: b2bStaff,
    isSuccess: b2bStaffLoaded,
    isError: b2bStaffFailed,
  } = useQuery({
    queryKey: posStaffQueryKey({ storeId: selectedStoreId, salesChannel: 'b2b', keyword: '' }),
    queryFn: () => posService.getStaffList({ limit: POS_STAFF_PAGE_LIMIT, salesChannel: 'b2b' }),
    enabled: isB2B,
    staleTime: 2 * 60 * 1000,
  });
  const b2bStaffIds = useMemo(() => (b2bStaff ?? []).map((s) => s.staff_id), [b2bStaff]);
  // Đổi chế độ B2C ↔ B2B thì hai danh sách chọn người khác hẳn nhau — hook lo phần đặt lại
  // `care_staff_id` (và bỏ qua khi chỉ là chuyển tab, xem docblock của hook).
  const { syncMode: syncCareStaffMode } = useCareStaffModeSync({
    form,
    isB2B,
    tabId: active.id,
    currentUserId: user?.id,
    currentUserIsB2BSales,
    b2bStaffIds,
    // Lỗi mạng cũng tính là "đã có kết quả": không xác thực được id khôi phục thì phải xoá,
    // còn hơn để lọt một người không thuộc danh sách NVKD vào đơn.
    b2bStaffResolved: b2bStaffLoaded || b2bStaffFailed,
  });

  // TCR-629: kho đã chọn per-line thuộc chi nhánh cũ — reset khi đổi chi nhánh.
  const prevStoreRef = useRef(selectedStoreId);
  useEffect(() => {
    if (prevStoreRef.current === selectedStoreId) return;
    prevStoreRef.current = selectedStoreId;
    setOrderItems((prev) =>
      prev.some((i) => i.warehouseId)
        ? prev.map((i) =>
            i.warehouseId ? { ...i, warehouseId: undefined, warehouseName: undefined } : i,
          )
        : prev,
    );
  }, [selectedStoreId, setOrderItems]);

  const {
    suggestion: comboSuggestion,
    blocked: comboBlocked,
    definition: comboDefinition,
    definitions: comboDefinitions,
    dismiss: dismissCombo,
    refresh: refreshCombos,
  } = useComboSuggestions({
    items: orderItems,
    isB2B,
    enabled: Boolean(selectedStoreId),
    tabId: active.id,
  });

  const findComboDefinition = useCallback(
    (comboGroupId: string) => {
      const comboId = orderItems.find((item) => item.comboGroupId === comboGroupId)?.comboId;
      return comboDefinitions.find((def) => def._id === comboId);
    },
    [orderItems, comboDefinitions],
  );

  /** TCR-982: cụm đang chờ tra tồn để tăng số combo — khoá ô SL của riêng nó. */
  const [comboCountPending, setComboCountPending] = useState<string | null>(null);
  // TCR-2303 — popup "Thêm nhanh hàng hóa" (chỉ đơn B2B; đơn thường vẫn dùng BulkQrModalButton).
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const quickAddCustomerName = Form.useWatch('customer_name', form) as string | undefined;

  /**
   * TCR-982 regression: số thứ tự request MỚI NHẤT đã khắc cho mỗi cụm combo.
   * `handleChangeComboCount` là async — nếu 2 lần gọi chồng nhau cho CÙNG một
   * cụm trước khi lần tra tồn đầu xong, response VỀ SAU (dù ứng với request cũ
   * hơn) không được phép ghi đè kết quả của request MỚI HƠN. Mỗi lần gọi tự
   * khắc số mới cho cụm nó đang sửa; trước khi áp kết quả, so với số MỚI NHẤT
   * hiện tại — lệch thì bỏ qua âm thầm (kết quả đúng do lần gọi mới quyết định).
   */
  const comboCountRequestSeq = useRef<Map<string, number>>(new Map());

  // TCR-983: gộp/tách cụm combo sinh ra dòng lẻ mới mà không nhìn dòng lẻ sẵn
  // có cùng biến thể → giỏ hiện hai dòng cùng SP (10 và 1) thay vì một dòng 11.
  // Gộp ở ĐÂY chứ không nhét vào trong `splitComboGroup`/`setComboGroupCount`:
  // màn sửa đơn dùng chung các util đó và map dòng về order item đã lưu theo
  // `id` — gộp mất một `id` là BE không biết dòng đó cần xoá.
  const handleApplyCombo = useCallback(() => {
    if (!comboSuggestion || !comboDefinition) return;
    setOrderItems((prev) =>
      mergeLooseDuplicates(
        applyComboSuggestion(prev, comboSuggestion, comboDefinition, fulfillmentMode, uuidv4),
        fulfillmentMode,
      ),
    );
  }, [comboSuggestion, comboDefinition, fulfillmentMode, setOrderItems]);

  const handleSplitCombo = useCallback(
    (comboGroupId: string) => {
      setOrderItems((prev) =>
        mergeLooseDuplicates(splitComboGroup(prev, comboGroupId, fulfillmentMode), fulfillmentMode),
      );
    },
    [setOrderItems, fulfillmentMode],
  );

  const handleRemoveCombo = useCallback(
    (comboGroupId: string) => {
      setOrderItems((prev) => removeComboGroup(prev, comboGroupId));
    },
    [setOrderItems],
  );

  /**
   * `setComboGroupCount` nhưng giỏ được dọn trùng ở CẢ HAI đầu.
   *
   * Hàm gốc tạo một dòng thành phần cho MỖI dòng lẻ nó rút vào cụm: hai dòng lẻ
   * cùng sản phẩm (rất thường gặp sau khi tự thêm hàng cho TCR-982, hoặc sau
   * một lần tách cụm) sinh ra hai dòng thành phần trùng nhau trong cụm. Gỡ cụm
   * → gộp dòng lẻ → mới gom lại, nên mỗi thành phần đúng MỘT dòng với số lượng
   * cộng dồn.
   */
  const setComboCountMerged = useCallback(
    (
      items: OrderItem[],
      comboGroupId: string,
      nextCount: number,
      definition: PosComboDefinition,
    ): OrderItem[] => {
      const loosened = mergeLooseDuplicates(
        splitComboGroup(items, comboGroupId, fulfillmentMode),
        fulfillmentMode,
      );
      return mergeLooseDuplicates(
        setComboGroupCount(loosened, comboGroupId, nextCount, definition, fulfillmentMode, uuidv4),
        fulfillmentMode,
      );
    },
    [fulfillmentMode],
  );

  /** Đổi số combo bằng đúng hàng ĐANG có trong giỏ — không chạm mạng. */
  const applyComboCountFromCart = useCallback(
    (comboGroupId: string, nextCount: number, definition: PosComboDefinition) => {
      setOrderItems((prev) => {
        try {
          return setComboCountMerged(prev, comboGroupId, nextCount, definition);
        } catch (error) {
          void message.warning(
            error instanceof Error ? error.message : 'Không đổi được số combo',
          );
          return prev;
        }
      });
    },
    [setOrderItems, setComboCountMerged],
  );

  /**
   * TCR-982: tăng số combo vượt hàng đang có trong giỏ.
   *
   * Luật cũ ("stepper không tự mua hộ khách") chặn cứng ở `setComboGroupCount`,
   * nên thu ngân gõ 12 thì ô trả về 1 dù kho còn đủ hàng. Nay: thiếu bao nhiêu
   * thì tra tồn TƯƠI của đúng các thành phần thiếu (`lookupVariants` — cùng cổng
   * chặn `addComboToCart` dùng), tự thêm phần thiếu vào giỏ, rồi mới gộp cụm.
   * Không đủ tồn → kẹp về số combo tồn cho phép và nói rõ lý do.
   *
   * `setComboGroupCount` GIỮ NGUYÊN throw — vẫn là bất biến chốt chặn cuối.
   */
  const handleChangeComboCount = useCallback(
    async (comboGroupId: string, nextCount: number) => {
      const definition = findComboDefinition(comboGroupId);
      if (!definition) {
        void message.error('Combo không còn hiệu lực, vui lòng làm mới giỏ hàng');
        return;
      }

      // TCR-982 regression: khắc số thứ tự MỚI cho cụm này — TRƯỚC cả nhánh
      // đồng bộ (deficits rỗng), để một lần gọi async đang treo phía sau nhận ra
      // nó đã bị một lần gọi mới hơn (kể cả đồng bộ) vượt qua khi tỉnh dậy.
      const mySeq = (comboCountRequestSeq.current.get(comboGroupId) ?? 0) + 1;
      comboCountRequestSeq.current.set(comboGroupId, mySeq);

      // Cùng lớp bug TCR-1096: hàm này `await lookupVariants` rồi mới ghi giỏ.
      // Thanh tab KHÔNG bị che gì trong lúc chờ (không có mask nào), thu ngân
      // bấm sang tab khác là hoàn toàn có thể — và `mySeq` chỉ chống đè giữa các
      // lần gọi CÙNG cụm, nó không biết gì về việc đổi tab. Chốt id tab đích
      // ngay đây để lần ghi muộn luôn rơi đúng giỏ đã đọc.
      const targetTabId = activeRef.current.id;

      const deficits = planComboGrowth(orderItems, comboGroupId, nextCount, definition);
      if (deficits.length === 0) {
        applyComboCountFromCart(comboGroupId, nextCount, definition);
        return;
      }

      // PREORDER bán hàng chưa có — không có tồn để chặn, nhất quán với
      // `findComboShortages`. Vẫn phải thêm dòng hàng nên cần lookup chi tiết.
      setComboCountPending(comboGroupId);
      try {
        const lookup = await posService.lookupVariants(
          deficits.map((d) => d.variantCode),
          fulfillmentMode,
        );

        // TCR-982 regression: điểm await DUY NHẤT của hàm này — từ đây tới
        // `setOrderItems` bên dưới không còn `await` nào khác, nên CHỈ CẦN kiểm
        // tra MỘT LẦN ở đây. Đã có lần gọi mới hơn cho CÙNG cụm này (đồng bộ
        // hoặc async khác đã tỉnh trước) → bỏ qua toàn bộ phần còn lại, không
        // toast (không phải lỗi, kết quả đúng do lần gọi mới quyết định).
        if (comboCountRequestSeq.current.get(comboGroupId) !== mySeq) return;

        let effectiveCount = nextCount;
        if (fulfillmentMode === 'INSTANT') {
          const stockByVariantId = new Map(
            lookup.items.map((item) => [item.variant.variant_id, item.stock_quantity]),
          );
          const maxByStock = maxComboCountByStock(
            orderItems,
            comboGroupId,
            definition,
            stockByVariantId,
          );
          effectiveCount = Math.min(nextCount, maxByStock);
          if (effectiveCount < 1) {
            void message.warning('Kho không còn đủ hàng để gộp thêm combo');
            return;
          }
          if (effectiveCount < nextCount) {
            void message.warning(`Chỉ đủ tồn cho ${effectiveCount} combo`);
          }
        }

        // Tính lại thiếu hụt theo số combo THỰC SỰ áp dụng — kẹp xuống rồi mà
        // vẫn thêm hàng theo số đã gõ là nhét thừa hàng vào giỏ khách.
        const finalDeficits =
          effectiveCount === nextCount
            ? deficits
            : planComboGrowth(orderItems, comboGroupId, effectiveCount, definition);

        setOrderItemsForTab(targetTabId, (prev) => {
          try {
            const filled = addComboDeficitLines(
              prev,
              finalDeficits,
              lookup.items,
              fulfillmentMode,
              uuidv4,
              comboGroupId,
            );
            return setComboCountMerged(filled, comboGroupId, effectiveCount, definition);
          } catch (error) {
            void message.warning(
              error instanceof Error ? error.message : 'Không đổi được số combo',
            );
            return prev;
          }
        });
      } catch (error) {
        void message.error(
          getApiError(error)?.message ?? 'Không tra được tồn kho để tăng số combo',
        );
      } finally {
        setComboCountPending(null);
      }
    },
    [
      findComboDefinition,
      orderItems,
      applyComboCountFromCart,
      setComboCountMerged,
      setOrderItemsForTab,
      fulfillmentMode,
    ],
  );

  const comboMaxCountFor = useCallback(
    (comboGroupId: string) => {
      const definition = findComboDefinition(comboGroupId);
      if (!definition) return 0;
      return maxComboCount(orderItems, comboGroupId, definition);
    },
    [findComboDefinition, orderItems],
  );

  const [successOpen, setSuccessOpen] = useState(false);
  const [successResult, setSuccessResult] = useState<CheckoutSuccessResult | null>(null);
  const [successTabId, setSuccessTabId] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutRequestLoading, setCheckoutRequestLoading] = useState(false);
  const tabInteractionsDisabled = checkoutRequestLoading || successOpen;

  // Đơn mới (tab trắng) không được thừa hưởng "Bỏ qua" của đơn/tab trước — combo
  // (combo_id, unit_count) từng bị bỏ qua ở nơi khác phải gợi ý lại bình thường
  // trên tab trắng này nếu giỏ mới cũng khớp. `addTab` không tự biết về combo
  // (Task 9), nên bọc nó ở đây thay vì sửa `usePosTabs`.
  const handleAddTab = useCallback(() => {
    if (tabInteractionsDisabled) return;
    // Review round 1, Finding 5: ở trần MAX_TABS, `addTab()` chỉ bắn toast "Tối
    // đa..." rồi return — KHÔNG có tab trắng nào được tạo, CHỦ ĐỀ vẫn là tab
    // hiện tại. Refresh trong trường hợp đó xoá oan "Bỏ qua" của tab đang mở,
    // banner combo vừa bị gạt bật lại ngay trước mắt thu ngân.
    const willAddTab = tabsState.tabs.length < MAX_TABS;
    addTab();
    if (willAddTab) refreshCombos();
  }, [addTab, refreshCombos, tabInteractionsDisabled, tabsState.tabs.length]);

  // Review round 1, Finding 2: đóng tab (nút đóng, F2, hoặc "Tiếp tục" trên
  // dialog thành công) cũng mở ra một đơn MỚI y hệt `handleAddTab` — tab cuối
  // cùng bị đóng thì `usePosTabs` tự dựng lại một tab trắng. Combo bị "Bỏ qua"
  // trên tab vừa đóng không được dính sang đơn trắng đó. Một wrapper DUY NHẤT
  // ở đây thay `closeTab` thô cho cả ba lối vào, thay vì refresh riêng lẻ ở
  // từng nơi gọi (dễ sót một lối, đúng như bản trước đã sót cả ba).
  const handleCloseTab = useCallback(
    (id: string) => {
      if (tabInteractionsDisabled) return;
      closeTab(id);
      refreshCombos();
    },
    [closeTab, refreshCombos, tabInteractionsDisabled],
  );
  // Đang chờ hãng trả mã vận đơn khi thu ngân bấm "Tạo và Đi đơn".
  const [dispatching, setDispatching] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  // Task 18: `payable` (chép tay công thức) đã bị xoá khỏi đây — nguồn số duy
  // nhất cho mọi khối tiền ở cột phải (Thông tin đơn hàng, Hình thức thanh
  // toán, COD/doanh thu) và cho payload gửi BE giờ là `money`, dựng ở
  // `useMemo` bên dưới (sau khi các biến `shipping*` đã watch xong) bằng
  // CHÍNH `computePosMoney` mà BE dùng khi ghi đơn.
  const [itemErrors, setItemErrors] = useState<Record<string, PosItemError>>({});
  const [checkoutAttempted, setCheckoutAttempted] = useState(false);
  const manualReconciliationAttempt =
    active.checkoutAttempt?.reconciliationStatus === 'manual_reconciliation';
  // Snapshot còn treo mà KHÔNG có trạng thái đối soát = lần ghi trước không
  // nhận được kết quả (mất mạng/5xx/timeout). Tab đang bị khoá nhưng màn hình
  // không có dấu hiệu gì — thu ngân chỉ thấy toast lạ mỗi lần bấm, và lối
  // thoát duy nhất trước đây là đóng tab, tức mất sạch giỏ hàng đã nhập.
  //
  // `!checkoutRequestLoading` là phần bắt buộc, không phải tinh chỉnh cho đẹp:
  // snapshot được ghi vào tab NGAY TRƯỚC `posService.createOrder` (xem
  // `handleCheckout`), nên thiếu vế này thì banner bật lên trong SUỐT thời gian
  // request đang bay — mọi lần ghi hoá đơn đều nháy "Đơn có thể đã vào sổ.
  // Kiểm tra danh sách đơn hàng…" cho chính cú bấm vừa xong, lúc BE còn chưa
  // trả lời (đo trên dev: banner hiện sau 6ms kể từ lúc bấm, request bị giữ lại
  // 12 giây vẫn chưa rời trình duyệt). Câu cảnh báo chỉ có nghĩa khi lần ghi ĐÃ
  // kết thúc mà không rõ kết quả — tức là sau khi `finally` hạ cờ này xuống.
  const unresolvedCheckoutAttempt =
    !!active.checkoutAttempt &&
    !active.checkoutAttempt.reconciliationStatus &&
    !checkoutRequestLoading;
  const prevActiveIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevActiveIdRef.current === active.id) return;
    form.resetFields();
    // Chế độ của tab vừa nạp (snapshot), KHÔNG phải chế độ đang hiển thị trước khi nạp.
    const restoredIsB2B = active.customerForm.user_type === UserType.ORGANIZATION;
    const restoredCareStaffId = active.customerForm.care_staff_id as string | undefined;
    if (Object.keys(active.customerForm).length > 0) {
      form.setFieldsValue(active.customerForm);
      // Snapshot khôi phục từ sessionStorage (tab để mở qua một lần deploy, hay
      // dữ liệu cũ khác) có thể mang cặp partner/label không còn hợp lệ —
      // `normalizeOrderLabel` (Task 6) chỉ chạy trong onChange đổi hãng của
      // CarrierPanel, không tự chạy khi form được nạp thẳng từ snapshot như thế
      // này. Chuẩn hoá lại NGAY ở đây, giống hệt như khi thu ngân tự tay đổi
      // hãng, để Select không hiện chữ thô không khớp option nào và checkout
      // không dính lỗi validate khó hiểu vì gửi nhãn rác cho hãng.
      const restoredPartner = active.customerForm.shipping_partner as string | undefined;
      const restoredLabel = active.customerForm.shipping_label as ShippingOrderLabel | undefined;
      const normalizedLabel = normalizeOrderLabel(restoredLabel, restoredPartner);
      if (normalizedLabel !== restoredLabel) {
        form.setFieldsValue({ shipping_label: normalizedLabel });
      }
      // TCR-2429 — tab B2B nạp lại mà chưa có người ghi nhận doanh số (vd. tab tạo trước
      // ticket) thì điền mặc định đúng chế độ, y như nhịp đổi chế độ: NVKD ⇒ chính họ, còn
      // quản lý/kế toán/owner thì để trống cho rule required bắt chọn. Đơn B2C giữ nguyên
      // hành vi cũ (không tự điền khi nạp tab).
      // Có sẵn id thì KHÔNG tin ngay — hook sẽ đối chiếu với danh sách NVKD (Codex finding 2).
      if (restoredIsB2B && !restoredCareStaffId) {
        const fallbackCareStaffId = defaultCareStaffIdForMode({
          isB2B: true,
          currentUserId: user?.id,
          currentUserIsB2BSales: currentUserIsB2BSales,
        });
        if (fallbackCareStaffId) form.setFieldsValue({ care_staff_id: fallbackCareStaffId });
      }
    } else if (user?.id) {
      // Tab HOÀN TOÀN MỚI (customerForm rỗng, xem createEmptyTab) — mặc định
      // nhân viên chăm sóc là người đang đăng nhập. `staff_id` trả về từ
      // GET /pos/staff CHÍNH LÀ User._id (xác nhận qua BE,
      // pos-staff.service.ts) — dùng thẳng `user.id`, không cần tra cứu.
      // TCR-2429: tab mới luôn mở ở chế độ B2C (`user_type` mặc định 'personal'), nên mặc định
      // chính mình vẫn đúng; đơn B2B chỉ hình thành khi thu ngân chọn khách tổ chức, và hook
      // `useCareStaffModeSync` sẽ đặt lại giá trị đúng chế độ ngay tại nhịp đổi chế độ đó.
      form.setFieldsValue({ care_staff_id: user.id });
    }
    // TCR-2429 — tab vừa nạp mang sẵn chế độ của chính nó; báo cho hook biết đây KHÔNG phải
    // "người dùng vừa đổi chế độ", nếu không nhịp render ngay sau đó sẽ đặt lại mặc định và xoá
    // mất `care_staff_id` đã lưu trong snapshot của tab. Kèm luôn id khôi phục để hook xác thực
    // lại với danh sách NVKD khi là đơn B2B.
    syncCareStaffMode({
      tabId: active.id,
      isB2B: restoredIsB2B,
      careStaffId: restoredCareStaffId,
    });
    // Mỗi tab là một đơn riêng — đổi tab thì cảnh báo "chưa chọn khách" của đơn
    // trước không được dính sang.
    setCheckoutAttempted(false);
    prevActiveIdRef.current = active.id;
  }, [active.id, active.customerForm, form, user?.id, currentUserIsB2BSales, syncCareStaffMode]);

  const handleSwitchTab = useCallback(
    (nextId: string) => {
      if (tabInteractionsDisabled) return;
      if (nextId === active.id) return;
      // getFieldsValue(true): snapshot cả field của CustomerDialog chưa mount,
      // nếu không thông tin khách sẽ mất khi quay lại tab.
      patchActive({ customerForm: form.getFieldsValue(true) });
      const nextTab = tabsState.tabs.find((tab) => tab.id === nextId);
      if (nextTab) activeRef.current = nextTab;
      switchTab(nextId);
    },
    [active.id, form, patchActive, switchTab, tabInteractionsDisabled, tabsState.tabs],
  );

  // Modal.confirm là API mệnh lệnh (không có node React để đọc trạng thái), nên
  // POSPage tự giữ cờ mở/đóng. Hotkey đổi tab gate theo cờ này thay vì dò
  // `.ant-modal-wrap` trong DOM — node đó KHÔNG bị gỡ khi Modal đóng.
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const confirmModal = useCallback((config: FulfillmentChangeConfirmConfig) => {
    setConfirmModalOpen(true);
    const release = () => setConfirmModalOpen(false);
    Modal.confirm({
      ...config,
      onOk: () => {
        release();
        config.onOk();
      },
      onCancel: release,
      afterClose: release,
    });
  }, []);

  const handleCloseTabRequest = useCallback(
    (id: string) => {
      if (tabInteractionsDisabled) return;
      const tab = tabsState.tabs.find((t) => t.id === id);
      if (!tab) return;
      const hasData =
        tab.orderItems.length > 0 ||
        Object.values(tab.customerForm).some((v) => v !== undefined && v !== '' && v !== null);
      if (!hasData) {
        handleCloseTab(id);
        return;
      }
      confirmModal({
        title: 'Đóng đơn này?',
        content: 'Dữ liệu trong đơn sẽ bị mất.',
        okText: 'Đóng',
        cancelText: 'Huỷ',
        onOk: () => handleCloseTab(id),
      });
    },
    [handleCloseTab, confirmModal, tabInteractionsDisabled, tabsState.tabs],
  );

  useEffect(() => {
    const flushOnHidden = () => {
      // Only flush khi tab/cửa sổ đang chuyển sang hidden; lúc visible lại sẽ
      // overwrite snapshot bằng giá trị có thể stale (vd nếu user vừa switch tab POS).
      if (document.visibilityState === 'hidden') {
        patchActive({ customerForm: form.getFieldsValue(true) });
      }
    };
    const flushOnUnload = () => {
      patchActive({ customerForm: form.getFieldsValue(true) });
    };
    document.addEventListener('visibilitychange', flushOnHidden);
    window.addEventListener('beforeunload', flushOnUnload);
    return () => {
      document.removeEventListener('visibilitychange', flushOnHidden);
      window.removeEventListener('beforeunload', flushOnUnload);
    };
  }, [form, patchActive]);

  const hotkeyOnClose = useCallback(
    () => handleCloseTabRequest(active.id),
    [handleCloseTabRequest, active.id],
  );
  const hotkeyOnSwitchByIndex = useCallback(
    (idx: number) => {
      const target =
        idx === -1 ? tabsState.tabs[tabsState.tabs.length - 1] : tabsState.tabs[idx];
      if (target) handleSwitchTab(target.id);
    },
    [tabsState.tabs, handleSwitchTab],
  );

  usePosTabsHotkeys({
    onNew: handleAddTab,
    onClose: hotkeyOnClose,
    onSwitchByIndex: hotkeyOnSwitchByIndex,
    // Overlay nào đang che màn hình thì F1/F2/Alt+N phải im — chúng đổi
    // `active.id` giữa lúc thu ngân đang nhập, và `checkoutLoading` là lúc request
    // tạo đơn đang bay: đổi tab khi đó thì dialog thành công sẽ đóng nhầm tab rỗng
    // vừa mở.
    //
    // Tất cả là state React. TUYỆT ĐỐI không quay lại dò `.ant-modal-wrap`:
    // node đó ở lại DOM sau khi Modal đóng → hotkey chết vĩnh viễn cả phiên POS.
    disabled: tabInteractionsDisabled || customerDialogOpen || confirmModalOpen,
  });

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  // EG-369: enforce shift presence. Auto-init at login covers cashier + 1-store users;
  // multi-store and zero-store cases land here.
  useEffect(() => {
    if (!ENFORCE_POS_SHIFT) return; // enforce mở ca đang tắt → cho vào POS thẳng
    if (getPosShiftStoreId()) return; // shift opened or auto-init succeeded
    const assignments = user?.storeAssignments ?? [];
    if (assignments.length === 0) {
      void message.error('Tài khoản chưa được gán chi nhánh nào. Liên hệ admin.');
      navigate('/');
      return;
    }
    // Multi-store with no shift → must pick via Shifts page
    navigate('/pos/shifts');
  }, [navigate, user]);

  // ===== TCR-1096: "Sao chép đơn hàng" từ màn chi tiết đơn =====
  //
  // `Orders/detail.tsx` điều hướng sang đây kèm `location.state.copyFromOrder`
  // (kết quả `mapOrderToPosPrefill`). KHÔNG có state đó — tức mọi lần mở /pos
  // bình thường — toàn bộ khối này là no-op: `copyFromOrder` undefined, effect
  // return ngay dòng đầu, không dispatch gì.
  const location = useLocation();
  // `location.state` sống trong history entry của TRÌNH DUYỆT: nó sống lâu hơn
  // một lần deploy, nên hình dạng của nó là dữ liệu KHÔNG tin được, không phải
  // hằng số của bản build này. Cast trần rồi `.some(...)` lên `orderItems`
  // undefined là vỡ cả màn POS, và nạn nhân phải tự xoá history mới vào lại
  // được. Sai hình dạng ⇒ coi như không có, im lặng bỏ qua.
  const customerPrefill = readPosCustomerPrefill(location.state);
  const copyFromOrder = readPosOrderPrefill(location.state);
  const customerPrefillAppliedRef = useRef(false);

  useEffect(() => {
    if (!hasOwnCustomerPrefill(location.state) || customerPrefill || copyFromOrder) return;
    navigate(location.pathname, {
      replace: true,
      state: removeCustomerPrefill(location.state),
    });
  }, [copyFromOrder, customerPrefill, location.pathname, location.state, navigate]);

  // Chỉ áp dụng MỘT lần cho mỗi lần mount: effect còn chạy lại vì `tabsState`
  // đổi (chính patch bên dưới gây ra), không có cờ này thì nhân bản tab vô hạn.
  const copyAppliedRef = useRef(false);

  useEffect(() => {
    if (!copyFromOrder || copyAppliedRef.current) return;
    copyAppliedRef.current = true;
    if (customerPrefill) customerPrefillAppliedRef.current = true;

    // Xoá state khỏi history NGAY: F5 trên /pos sẽ replay `location.state` (nó
    // nằm trong history entry, không phải bộ nhớ React) và mồi thêm một tab nữa.
    const clearNavState = () => navigate(location.pathname, { replace: true, state: null });

    // Ở trần MAX_TABS, `addTab()` chỉ bắn toast rồi return — KHÔNG có tab mới.
    // Patch khi đó sẽ ghi đè thẳng lên giỏ hàng của tab thu ngân đang làm dở,
    // đúng thứ tuyệt đối không được xảy ra. Dừng lại và nói rõ lý do.
    if (tabsState.tabs.length >= MAX_TABS) {
      void message.warning(
        `Tối đa ${MAX_TABS} đơn cùng lúc — đóng bớt một đơn rồi sao chép lại.`,
      );
      clearNavState();
      return;
    }

    handleAddTab();
    // `addTab` và `patchActive` là hai `dispatch` của CÙNG một `useReducer`
    // trong cùng một tick: reducer xử lý tuần tự theo thứ tự dispatch, nên
    // PATCH_ACTIVE nhắm vào `activeId` SAU ADD_TAB — tức tab vừa thêm. Biến
    // `active` trong closure này vẫn là tab cũ (state của render hiện tại), nên
    // KHÔNG được đọc nó để quyết định patch vào đâu.
    patchActive({
      orderItems: copyFromOrder.orderItems,
      customerForm: customerPrefill
        ? { ...copyFromOrder.customerForm, ...customerPrefill }
        : copyFromOrder.customerForm,
    });
    // KHÔNG gọi `form.setFieldsValue` ở đây: `active.id` vừa đổi nên effect nạp
    // form theo tab (`prevActiveIdRef`, phía trên) sẽ chạy ở render kế tiếp,
    // `form.resetFields()` rồi `setFieldsValue(active.customerForm)` — chính
    // snapshot vừa patch. Ghi thêm ở đây chỉ để bị `resetFields` đó xoá.
    clearNavState();
  }, [copyFromOrder, tabsState.tabs.length, handleAddTab, patchActive, navigate, location.pathname]);

  useEffect(() => {
    if (!customerPrefill || customerPrefillAppliedRef.current) return;
    customerPrefillAppliedRef.current = true;

    const clearNavState = () => navigate(location.pathname, { replace: true, state: null });
    const currentTabs = tabsRef.current;
    if (currentTabs.length >= MAX_TABS) {
      void message.warning(`Tối đa ${MAX_TABS} đơn cùng lúc — đóng bớt một đơn rồi chọn lại khách.`);
      clearNavState();
      return;
    }

    const currentTab = currentTabs.find((tab) => tab.id === activeRef.current.id);
    if (!currentTab) {
      clearNavState();
      return;
    }

    const currentForm = form.getFieldsValue(true);
    const activeTabHasWork =
      currentTab.orderItems.length > 0 ||
      hasUsefulCustomerForm(currentTab.customerForm) ||
      hasUsefulCustomerForm(currentForm);

    if (!activeTabHasWork) {
      patchActive({ customerForm: customerPrefill });
      form.setFieldsValue(customerPrefill);
      clearNavState();
      return;
    }

    handleAddTab();
    patchActive({ customerForm: customerPrefill });
    clearNavState();
  }, [customerPrefill, form, handleAddTab, location.pathname, navigate, patchActive]);

  const addItem = useCallback(
    (variant: PosVariant) => {
      const newItem = variantToOrderItem(variant, fulfillmentMode);

      setOrderItems((prev) => {
        const isSerial = variant.tracking_mode === 'serial';
        const isInstant = fulfillmentMode === 'INSTANT';

        if (isSerial && isInstant) {
          if (prev.find((i) => i.specificationCode === variant.specification_code)) {
            void message.warning(
              `Serial ${variant.specification_code ?? variant.variant_code} đã có trong đơn`,
            );
            return prev;
          }
          return [...prev, recalcItem(newItem, fulfillmentMode)];
        }

        // CRITICAL (combo ảo): gộp CHỈ vào dòng LẺ, và chỉ vào dòng lẻ ĐẦU TIÊN.
        //
        // Trước tính năng này, hai dòng cùng `variantId` là bất khả thi nên
        // `prev.map(...)` trên mọi dòng khớp là vô hại. Combo ảo tạo ra chúng
        // CÓ CHỦ ĐÍCH: `applyComboSuggestion` tách một dòng dùng-một-phần thành
        // dòng combo + dòng lẻ dư, `setComboGroupCount` cũng vậy. Với mảng cũ:
        //
        //  - Giỏ 5×KCN-50, gộp combo → A(combo, 4) + A(lẻ, 1). MỘT lần quét mã
        //    đẩy CẢ HAI lên 5 và 2 — bảy đơn vị sau một lần quét, và khách bị
        //    tính tiền đủ bảy mà không có lỗi nào hiện ra.
        //  - Cộng vào dòng combo làm `quantity` lệch khỏi `comboUnitCount`:
        //    dòng nhóm và footer hiện hai tổng khác nhau cho cùng một cụm; lúc
        //    thanh toán BE 400, và handler lỗi tách MỌI cụm trong giỏ (phá
        //    chiết khấu của các cụm không liên quan). Tệ hơn: nếu số lượng rơi
        //    đúng bội số sạch thì nó qua verify ở một `unit_count` khác và thu
        //    thừa tiền trong im lặng.
        //
        // Muốn thêm hàng vào một cụm combo thì dùng stepper số combo — đó là
        // đường duy nhất giữ được bất biến `quantity == comboUnitQty × count`.
        //
        // TCR-1143 (final review round 3): cũng loại dòng flash sale
        // (`flash_sale_id`) và dòng quà tặng (`is_gift`) khỏi phép gộp —
        // mirror đúng `isMergeable()` ở `POS/utils/mergeLooseDuplicates.ts`.
        // Không loại 2 field này thì quét/bấm lại ĐÚNG variant đang có dòng
        // flash sale/quà trong giỏ sẽ ÂM THẦM cộng dồn SL vào đúng dòng đó
        // thay vì tách dòng mới — nhẹ hơn oversell ở `Orders/edit.tsx` (BE
        // `evaluate()`/tạo đơn ở POS vẫn verify lại đầy đủ
        // reservation/`max_per_customer` bất kể dòng gộp kiểu gì, nên không
        // thất thoát tiền/tồn), nhưng vẫn là một dòng flash sale "nuốt" một
        // lần quét thường một cách không tường minh — sai cùng lớp với bug
        // đã vá ở `Orders/edit.tsx`, chỉ khác mức nghiêm trọng.
        const looseIndex = prev.findIndex(
          (i) =>
            i.variantId === variant.variant_id &&
            !i.comboGroupId &&
            !i.flash_sale_id &&
            !i.is_gift &&
            // TCR-2614: thêm SKU luôn cộng vào dòng gốc, không vào dòng tách.
            !i.splitLine &&
            // Dòng giá 0 (hàng tặng) không nhận SL cộng dồn dù mất cờ splitLine.
            i.price !== 0,
        );
        if (looseIndex > -1) {
          const next = [...prev];
          next[looseIndex] = recalcItem(
            { ...next[looseIndex], quantity: next[looseIndex].quantity + 1 },
            fulfillmentMode,
          );
          return next;
        }
        return [...prev, recalcItem(newItem, fulfillmentMode)];
      });

      void message.success(`Đã thêm: ${variant.variant_name}`, 1);
    },
    [fulfillmentMode, setOrderItems],
  );

  const addFlashSaleItem = useCallback(
    (variant: PosVariant, flashSaleItemId: string, flashPrice: number) => {
      // TCR-1143: KHÔNG gộp vào dòng thường cùng variant (giá khác nhau) và
      // KHÔNG gộp vào dòng flash sale cùng item đã có (max_per_customer/
      // remaining_quantity được BE verify lại chính xác ở evaluate()/tạo đơn —
      // luôn thêm dòng mới đơn giản hơn và an toàn hơn cố gộp quantity ở đây).
      const baseItem = variantToOrderItem(variant, fulfillmentMode);
      const flashOrderItem = recalcItem(
        {
          ...baseItem,
          price: flashPrice,
          retailPrice: variant.price,
          totalAmount:
            baseItem.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER'
              ? flashPrice * baseItem.quantity
              : flashPrice,
          flash_sale_id: flashSaleItemId,
        },
        fulfillmentMode,
      );
      setOrderItems((prev) => [...prev, flashOrderItem]);
      void message.success(`Đã thêm (giá flash sale): ${variant.variant_name}`, 1);
    },
    [fulfillmentMode, setOrderItems],
  );

  const addCombo = useCallback(
    (combo: PosComboSearchItem, details: PosVariantDetailItem[]) => {
      // `addComboToCart` có thể ném lỗi (thiếu chi tiết sản phẩm, không tính
      // được giá phân bổ). Nó chạy BÊN TRONG updater của `setOrderItems`, nên
      // exception ném thẳng ra sẽ bung ở render phase → error boundary → mất
      // trắng giỏ hàng, thay vì một toast lỗi. Bọc try/catch NGAY TRONG updater
      // (khuôn giống `handleChangeComboCount` ở trên) và trả `prev` nguyên vẹn
      // khi lỗi — `message` gọi ở nhánh catch của updater, không phải trong
      // render phase, nên an toàn.
      let addedComboName: string | null = null;
      setOrderItems((prev) => {
        try {
          const next = addComboToCart(prev, combo, details, fulfillmentMode, uuidv4);
          addedComboName = combo.combo_name;
          return next;
        } catch (error) {
          void message.error(
            error instanceof Error ? error.message : 'Không thêm được combo vào giỏ hàng',
          );
          return prev;
        }
      });
      if (addedComboName) {
        // Combo toàn thành phần giá 0 (VD: quà tặng/khuyến mãi) → `combo.saving`
        // (retail_total - combo_price) ÂM hoặc bằng 0. Không hiện số "tiết kiệm"
        // trong trường hợp đó — cùng nguyên tắc guard `saving > 0` đang dùng ở
        // `QRComboSuggestItem`, tránh toast phi lý kiểu "tiết kiệm -680.000 ₫".
        void message.success(
          combo.saving > 0
            ? `Đã thêm combo: ${addedComboName} — tiết kiệm ${formatVND(combo.saving)}`
            : `Đã thêm combo: ${addedComboName}`,
          1,
        );
      }
    },
    [fulfillmentMode, setOrderItems],
  );

  /**
   * TCR-2303 — đưa cả lô từ popup "Thêm nhanh hàng hóa" vào giỏ trong MỘT lần ghi.
   *
   * KHÔNG gọi `addItem` N lần rồi `updateItem` N lần: mỗi lần là một `setOrderItems` riêng, dễ
   * lệch id/ghi đè nhau và cho N toast. `mergeQuickAddIntoCart` dựng cả lô (gộp vào dòng lẻ đầu
   * tiên, flash chia lại suất, giữ giá/CK/VAT NVBH sửa kèm cờ bảo vệ) rồi ta ghi một lần.
   *
   * TCR-2407 — combo tick từ lưới đi đường RIÊNG trước khi merge: `appendComboPicksToCart` dựng
   * cụm bằng `addComboToCart` (mỗi thành phần 1 dòng chung `comboGroupId`, giá từ
   * `resolveComboLinePrices`) — đi qua `mergeQuickAddIntoCart` sẽ làm mất nhóm combo. Combo vào
   * giỏ TRƯỚC nên `findMergeTargetIndex` (loại dòng thuộc cụm) không bao giờ cộng dồn dòng lẻ
   * vào dòng thành phần combo.
   */
  const handleQuickAddConfirm = useCallback(
    (entries: QuickAddEntry[], combos: QuickAddComboPick[]) => {
      if (entries.length === 0 && combos.length === 0) return;
      let result = null as ReturnType<typeof mergeQuickAddIntoCart> | null;
      let comboResult = null as ReturnType<typeof appendComboPicksToCart> | null;
      setOrderItems((prev) => {
        comboResult = appendComboPicksToCart(prev, combos, fulfillmentMode);
        result = mergeQuickAddIntoCart(comboResult.items, entries, fulfillmentMode);
        return result.items;
      });
      setQuickAddOpen(false);
      const done = result as ReturnType<typeof mergeQuickAddIntoCart> | null;
      const combosDone = comboResult as ReturnType<typeof appendComboPicksToCart> | null;
      // Combo dựng lỗi ở cụm nào thì báo riêng (hiếm — popup đã kiểm lúc tick);
      // các cụm còn lại và phần mã phẳng vẫn vào giỏ bình thường.
      for (const msg of combosDone?.failedCombos ?? []) void message.error(msg);
      if (done) {
        const parts = [`Đã thêm ${done.addedCount} mã mới`];
        if (done.mergedCount > 0) parts.push(`cộng dồn ${done.mergedCount} mã vào dòng có sẵn`);
        if (combosDone && combosDone.addedCount > 0) parts.push(`thêm ${combosDone.addedCount} cụm combo`);
        if (done.skippedCodes.length > 0) parts.push(`bỏ qua ${done.skippedCodes.length} mã serial`);
        void message.success(parts.join(', '));
      } else if (combosDone && combosDone.addedCount > 0) {
        void message.success(`Đã thêm ${combosDone.addedCount} cụm combo`);
      }
    },
    [fulfillmentMode, setOrderItems],
  );

  const updateItem = useCallback((id: string, patch: Partial<OrderItem>) => {
    setOrderItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
    // Vừa điền thứ đang báo đỏ thì xoá cờ lỗi ngay, không đợi bấm ghi hoá đơn lại.
    if (
      patch.sellerStaffId !== undefined ||
      patch.warehouseStaffId !== undefined ||
      patch.warehouseId !== undefined
    ) {
      setItemErrors((prev) => {
        const cur = prev[id];
        if (!cur) return prev;
        const next: PosItemError = { ...cur };
        if (patch.sellerStaffId) delete next.seller;
        if (patch.warehouseStaffId) delete next.warehouse;
        if (patch.warehouseId) delete next.warehousePick;
        const rest = { ...prev };
        if (!next.seller && !next.warehouse && !next.warehousePick) delete rest[id];
        else rest[id] = next;
        return rest;
      });
    }
  }, [setOrderItems]);

  // TCR-1423: mỗi lần thu ngân đổi SL ở BẤT KỲ dòng nào của nhóm flash sale
  // (dòng flash gốc hoặc dòng dư sinh ra từ lần tách trước), phân bổ lại suất
  // KM cho CẢ NHÓM — phần trong `flashRemaining` giữ giá flash, phần vượt về
  // giá gốc, gộp lại thành 1 dòng khi tổng SL trở về trong hạn mức. Xem
  // `rebalanceFlashGroup`.
  const handleRebalanceFlashGroup = useCallback(
    (id: string, requestedQuantity: number) => {
      setOrderItems((prev) => {
        const next = rebalanceFlashGroup(prev, id, requestedQuantity, fulfillmentMode, uuidv4);
        return next ?? prev;
      });
    },
    [fulfillmentMode, setOrderItems],
  );

  /**
   * Thu ngân bỏ giá flash của một dòng — dòng về giá niêm yết và mất mọi dấu
   * vết flash.
   *
   * Dùng CHUNG `dropFlashPrice` với ca "BE báo hết suất lúc ghi hoá đơn": hai
   * đường cho ra cùng một hình dạng dòng, nếu không đơn mang
   * `flash_sale_item_id` mồ côi trỏ tới suất không còn tồn tại.
   */
  const dropFlashOnItem = useCallback((id: string) => {
    setOrderItems((prev) => prev.map((item) => (item.id === id ? dropFlashPrice(item) : item)));
  }, []);

  // TCR-2614 — "Tách dòng": chèn dòng cùng SKU/kho, SL 1, giá 0 ngay dưới dòng gốc để bán
  // 2 dòng khác giá (vd 160 cái giá 295.000 + 5 cái hàng tặng giá 0). Dòng tách mang cờ
  // `splitLine` nên không bị gộp lại / cộng dồn / ghi đè giá bởi bảng giá B2B.
  const splitItem = useCallback(
    (id: string) => {
      setOrderItems((prev) => splitItemLine(prev, id, uuidv4, fulfillmentMode));
    },
    [fulfillmentMode, setOrderItems],
  );

  const removeItem = useCallback(
    (id: string) => {
      // TCR-1074 final review (Fix #4): tính `target`/gọi `form.setFieldsValue`
      // TRƯỚC khi gọi `setOrderItems`, không nhét vào bên trong updater —
      // updater của `setOrderItems` PHẢI thuần (chỉ tính `next` từ `prev`), gọi
      // side-effect (`form.setFieldsValue`) bên trong nó là antipattern dù an
      // toàn ở đây (đã trace kỹ StrictMode double-invoke: lần gọi thứ hai đọc
      // state đã prune sẵn nên no-op). Đọc `activeRef.current.orderItems` chứ
      // không phải biến `orderItems` — cùng lý do đã ghi ở `liveItems` phía
      // dưới: `orderItems` là snapshot lúc render, ref mới là giỏ SỐNG.
      const target = activeRef.current.orderItems.find((i) => i.id === id);
      // `isVoucherGiftItem`, KHÔNG chỉ `is_gift` thô: gỡ một dòng quà CTKM
      // (`gift_source: 'promotion'`) không được đụng tới voucher nào của
      // khách — hai nguồn quà độc lập nhau (xem doc comment `gift_source`
      // ở `types.ts`).
      if (target && isVoucherGiftItem(target) && target.gift_promotion_id) {
        const currentVouchers = (form.getFieldValue('vouchers') as VoucherItem[] | undefined) ?? [];
        const prunedVouchers = pruneVouchersForRemovedGift(currentVouchers, target.gift_promotion_id);
        if (prunedVouchers !== currentVouchers) {
          form.setFieldsValue({
            vouchers: prunedVouchers,
            voucher_discount: sumVoucherDiscount(prunedVouchers),
          });
        }
      }
      setOrderItems((prev) => prev.filter((i) => i.id !== id));
    },
    [setOrderItems, form],
  );

  // Task 6 — banner "đơn đủ điều kiện nhận quà" (BXGY/CTKM). `free_items`
  // đến từ `CustomerSidebar` (nơi DUY NHẤT gọi `/pos/promotions/evaluate`)
  // qua `onFreeItemsChange`. Fix round 1 (I4): handler + `pendingGifts` gộp
  // vào `usePromotionGiftAdd` — dùng chung với `Orders/edit.tsx`, tránh hai
  // bản chép tay có thể lệch nhau (đã từng lệch: bản `Orders/edit.tsx` thiếu
  // `setOrderItems` trong dep array).
  const [promotionFreeItems, setPromotionFreeItems] = useState<PromotionFreeItem[]>([]);
  // TCR-1403 — xem doc comment `onAppliedPromotionIdsChange` (CustomerSidebar)
  // + `appliedPromotionIds` (usePromotionGiftAdd). `null` ban đầu = chưa có
  // tín hiệu evaluate nào.
  const [appliedPromotionIds, setAppliedPromotionIds] = useState<string[] | null>(null);
  const {
    pending: giftPending,
    addPromotionGift: handleAddPromotionGift,
    pendingGifts,
    giftPickerRequest,
    resolvePick: handleGiftPickerSelect,
  } = usePromotionGiftAdd({
    selectedStoreId,
    fulfillmentMode,
    orderItems,
    promotionFreeItems,
    setOrderItems,
    appliedPromotionIds,
  });

  const [swapping, setSwapping] = useState(false);
  const swapItemSerial = useCallback(
    async (itemId: string, serial: string): Promise<{ ok: boolean; error?: string }> => {
      const item = orderItems.find((i) => i.id === itemId);
      if (!item) return { ok: false, error: 'Không tìm thấy dòng hàng' };
      // Serial là unique theo tenant → chặn nếu serial này đã nằm ở dòng khác trong đơn.
      if (orderItems.some((i) => i.id !== itemId && i.specificationCode === serial)) {
        return { ok: false, error: `Serial ${serial} đã có trong đơn` };
      }
      setSwapping(true);
      try {
        const qr = `${item.variantCode}_${serial}`;
        const { items, item_errors } = await posService.lookupVariants([qr], fulfillmentMode);
        if (item_errors.length > 0) return { ok: false, error: item_errors[0].error };
        if (items.length === 0) return { ok: false, error: 'Không tìm thấy serial' };
        const variant = mapDetailToPosVariant(items[0]);
        setOrderItems((prev) =>
          prev.map((i) => (i.id === itemId ? applySwappedVariant(i, variant, fulfillmentMode) : i)),
        );
        return { ok: true };
      } catch (err) {
        return { ok: false, error: getApiError(err)?.message ?? 'Lỗi khi đổi mã quy cách' };
      } finally {
        setSwapping(false);
      }
    },
    [orderItems, fulfillmentMode, setOrderItems],
  );

  const handleFulfillmentChange = (mode: FulfillmentMode) => {
    handleFulfillmentModeChange(mode, {
      form,
      orderItemsCount: orderItems.length,
      confirm: confirmModal,
      // Chốt id tab NGAY LÚC user đổi chế độ. `onOk` của confirm chạy muộn tuỳ ý
      // và nó xoá sạch giỏ — xem chú thích trong `fulfillmentChange.ts`.
      tabId: active.id,
      patchTab,
    });
  };

  // Tab/giỏ hàng còn trống mà `fulfillmentMode` không còn nằm trong cấu hình
  // tenant hiện tại (đổi qua Settings) → tự đưa về chế độ hợp lệ đầu tiên.
  // KHÔNG đụng vào giỏ hàng đã có sản phẩm — thu ngân đang dở việc không bị
  // đổi chế độ ngầm dưới chân (xem spec "không xử lý đặc biệt" cho case này).
  //
  // Finding 1 (review cuối branch): PHẢI đợi `generalConfigQuery.isSuccess`
  // trước khi hành động. Không có persister cho query này nên mọi cold load
  // (F5) bắt đầu với cache rỗng — `generalConfigQuery.data` là `undefined` cho
  // đến khi query resolve, và `allowedSalesModes` tạm rơi về
  // `DEFAULT_SALES_MODES` (['INSTANT']) trong lúc chờ. Nếu effect chạy ngay ở
  // lần render đó, nó sẽ thấy một tab trắng hydrate PREORDER từ sessionStorage
  // "không hợp lệ" theo default tạm thời và flip về INSTANT VĨNH VIỄN — khi
  // config thật resolve sau đó (có thể cho phép cả 2 mode) không có gì đảo lại
  // được nữa. Guard thứ hai (`allowedSalesModes.length === 0`) phòng trường
  // hợp dữ liệu server rỗng/hỏng không bao giờ được đẩy vào
  // `handleFulfillmentChange(undefined)`.
  useEffect(() => {
    if (!generalConfigQuery.isSuccess) return;
    if (allowedSalesModes.length === 0) return;
    if (orderItems.length === 0 && !allowedSalesModes.includes(fulfillmentMode)) {
      handleFulfillmentChange(allowedSalesModes[0]);
    }
  }, [generalConfigQuery.isSuccess, allowedSalesModes, fulfillmentMode, orderItems.length]);

  const handleSessionChange = (sessionId: string, staff: CommissionStaff[]) => {
    setSelectedSessionId(sessionId);
    setCommissionStaff(staff);
  };

  const handleCheckout = async (options?: { dispatchShipping?: boolean; saveAsDraft?: boolean }) => {
    // Đánh dấu đã bấm ghi hoá đơn ít nhất một lần → sidebar mới bật cảnh báo đỏ
    // "chưa chọn khách hàng". Trước đó giỏ trống khách là trạng thái bình thường.
    setCheckoutAttempted(true);

    // TCR-2129 — chặn cả tạo đơn lẫn "Lưu nháp" khi còn dòng CK khác chính sách chưa có lý do.
    if (discountPolicyEnabled) {
      const missing = findMissingDiscountReasons(orderItems, fulfillmentMode);
      if (missing.length > 0) {
        void message.error(`Còn ${missing.length} dòng chiết khấu khác chính sách chưa nhập lý do`);
        document.querySelector<HTMLInputElement>('[data-testid="discount-reason-input"]')?.focus();
        return;
      }
    }

    const checkoutTabId = activeRef.current.id;
    const initialFormValues = {
      ...(form.getFieldsValue(true) as Record<string, unknown>),
    };
    const initialCartSignature = cartSignature(orderItems);
    const initialInputSignature = checkoutInputSignature({
      formValues: initialFormValues,
      cartSignature: initialCartSignature,
      fulfillmentMode,
      orderType,
      paymentMethod,
      paymentAccountId,
      nonCashAmount,
      cashReceived,
      selectedSessionId,
      commissionStaff,
      saveAsDraft: options?.saveAsDraft,
    });
    const existingAttempt = activeRef.current.checkoutAttempt;
    if (existingAttempt?.reconciliationStatus === 'manual_reconciliation') {
      message.error('Khuyến mãi cần đối soát thủ công. Vui lòng xem đơn hàng hoặc liên hệ quản trị viên.');
      return;
    }
    if (
      existingAttempt &&
      (existingAttempt.cartSignature !== initialCartSignature ||
        existingAttempt.inputSignature !== initialInputSignature)
    ) {
      message.error(checkoutLockMessage(existingAttempt));
      return;
    }

    // Kiểm giỏ hàng. `orderItems` ở đây cũng chính là mảng đi vào payload —
    // validate và gửi đi cùng MỘT snapshot của closure. Nhưng snapshot đó có thể
    // đã LỆCH với giỏ trên màn hình lúc thực sự gọi API, nên còn một lần đối
    // chiếu nữa ngay trước `createOrder` (xem `validatedCartSignature`).
    const errs = validatePosItems(orderItems, fulfillmentMode);
    setItemErrors(errs);
    const validatedCartSignature = cartSignature(orderItems);
    const hasItemErrors = Object.keys(errs).length > 0;

    const hasCustomer = !!form.getFieldValue('customer_phone');

    // Chưa gắn khách: KHÔNG bật dialog "Thêm khách hàng" — chỉ card đỏ ở sidebar
    // + toast. Mở form trống ngay khi bấm là quá đột ngột, để thu ngân tự chọn
    // khách có sẵn hoặc bấm nút + khi họ muốn.
    //
    // Đã có khách nhưng còn trường trống: các field khách/giao nhận/hoá đơn nằm
    // trong CustomerDialog lazy-mount, mà antd chỉ validate (và tô đỏ) field ĐANG
    // MOUNT — phải mở dialog trước rồi đợi commit xong mới validateFields, không
    // thì lỗi hoá đơn lọt qua và đơn bị BE chặn.
    //
    // Ngoại lệ: `delivery_days` render sẵn ngoài sidebar nên đỏ ngay tại chỗ —
    // thiếu MỖI ô đó thì không bật dialog. [BỎ N.BÁN + NV CHĂM SÓC] `care_staff_id`
    // TỪNG là ngoại lệ thứ hai ở đây nhưng ô đó đã bị gỡ khỏi UI (xem
    // `CustomerSidebar/index.tsx`) và `missingCustomerFields` không còn kiểm nó,
    // nên nó không bao giờ xuất hiện trong `missingCustomerInfo` nữa — không còn
    // là ngoại lệ, chỉ là trường hợp không bao giờ xảy ra.
    const needsDialog =
      hasCustomer && requiresCustomerDialog(missingCustomerInfo.map((m) => m.name));
    if (needsDialog) {
      sidebarRef.current?.openCustomerDialog('edit');
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (activeRef.current.id !== checkoutTabId) {
      message.error('Đơn đang xử lý — tab đã chuyển, không thể ghi đơn từ dữ liệu khác. Vui lòng quay lại tab cũ và thử lại.');
      return;
    }

    let errorFields: FormErrorField[] = [];
    try {
      await form.validateFields();
    } catch (e) {
      errorFields = ((e as { errorFields?: FormErrorField[] }).errorFields ?? []).filter(Boolean);
    }

    // Mọi luật chặn nằm trong `runCheckoutGates` (hàm thuần, có test riêng); ở đây
    // chỉ còn phần side effect: kéo màn hình tới chỗ sai và bắn toast.
    //
    // Cổng ĐVVC nằm trong đó luôn. Trước đây nó phải ở bước xác nhận trong Drawer
    // vì hãng vận chuyển + khối lượng gói CHỈ nhập được bên trong Drawer — đặt ở
    // cổng mở Drawer thì thành thế bí. `CarrierPanel` giờ hiện thẳng ở cột giữa
    // nên chặn tại nút là được, thu ngân sửa ngay tại chỗ (ô đã tô đỏ vì
    // `showErrors={checkoutAttempted}`).
    const gate = runCheckoutGates({
      itemCount: orderItems.length,
      itemErrors: errs,
      errorFields,
      missing: missingCustomerInfo,
      hasCustomer,
      // `getFieldsValue(true)`: `shipping_partner` / `package_weight` không thuộc
      // Form.Item nào đang mount ở chế độ nhận tại quầy.
      values: form.getFieldsValue(true) as CustomerCheckoutValues,
      customerValidationPolicy,
      // Final review fix C1: cổng ĐVVC bên trong `runCheckoutGates` phải chuẩn
      // hoá `shipping_mode` bằng ĐÚNG setting tenant đang dùng để dựng payload
      // (`buildCreateOrderPayload` bên dưới) — không thì đơn giao hàng CHƯA bấm
      // thanh PTNH lọt cổng dù ghi ra hiệu lực là 'carrier'.
      defaultFulfillmentChoice,
    });

    if (!gate.ok) {
      if (hasCustomer && errorFields.length > 0) {
        // Mở lại dialog kể cả khi không `needsDialog` — trường đã mount từ lần mở
        // trước vẫn có thể sai định dạng, user cần thấy ô đỏ ở đâu. Vẫn bỏ qua
        // nếu lỗi chỉ nằm ở các ô sidebar.
        if (requiresCustomerDialog(errorFields.map((f) => String(f.name[0])))) {
          sidebarRef.current?.openCustomerDialog('edit');
        }
        form.scrollToField(errorFields[0].name, { behavior: 'smooth', block: 'center' });
      } else if (hasItemErrors) {
        const firstId = Object.keys(errs)[0];
        setTimeout(() => scrollToVisibleItemRow(firstId), 50);
      }
      showCheckoutProblems(gate.problems);
      return;
    }

    setCheckoutLoading(true);
    try {
      // getFieldsValue(true): customer/invoice fields live in the lazy-mounted
      // CustomerDialog. A no-arg getFieldsValue() only returns currently-mounted
      // Form.Items, so a search-selected-but-never-opened customer would build
      // this payload with blank customer fields.
      const values = { ...initialFormValues };

      // Strip FE-only label cache fields and the same-as-delivery checkbox.
      delete values._cityName;
      delete values._wardName;
      delete values._deliveryCityName;
      delete values._deliveryWardName;
      delete values.invoice_same_as_delivery;
      delete values.delivery_same_as_customer;
      delete values.customer_tax_code;
      delete values.has_delivery;

      const userType = (values.user_type as UserType) ?? UserType.PERSONAL;
      const receiveMethod = (values.receive_method as 'delivery' | 'pickup' | 'vault') ?? 'delivery';
      const derived = deriveDeliverySnapshot({
        user_type: userType,
        customer_name: values.customer_name as string | undefined,
        citizen_id: values.citizen_id as string | undefined,
        buyer_name: values.buyer_name as string | undefined,
        buyer_cccd: values.buyer_cccd as string | undefined,
        invoice_name: values.invoice_name as string | undefined,
      });
      values.customer_name = derived.customer_name;
      values.citizen_id = derived.citizen_id;

      // review round 3 (finding C): KM tự động debounce 500ms rồi mới gọi
      // `/pos/promotions/evaluate` (`CustomerSidebar`) — bấm "Ghi hoá đơn" ngay
      // trong lúc đó thì `promotion_discount` đang hiện trên màn hình chưa
      // chắc là số cuối cùng. Chặn ở đây, cùng tinh thần với cổng voucher
      // revalidate ngay bên dưới, TRƯỚC khi build payload — thà thu ngân bấm
      // lại còn hơn ghi đơn với khuyến mãi chưa chốt.
      if (values.promotion_evaluating === true) {
        message.warning('Đang kiểm tra khuyến mãi tự động áp dụng, vui lòng đợi giây lát rồi bấm lại.');
        return;
      }

      const promotionSnapshotId = values.promotion_snapshot_id as string | undefined;

      // Task 7b fix round 2 — biên đọc thứ tư: MỌI nơi đọc `shipping_mode` trong
      // file này phải dùng giá trị đã CHUẨN HOÁ (`effectiveShippingMode`), không
      // phải giá trị thô trong Form — đơn giao hàng có thể CHƯA BAO GIỜ được bấm
      // vào thanh 4 nút (thu ngân chỉ gõ thẳng vào ô "Phí vận chuyển"/"Khách trả
      // trước" mà round 1 vừa làm hiện lại), nên `values.shipping_mode` thô có
      // thể rỗng dù đơn vẫn đang giao hàng. Tính MỘT LẦN ở đây (`shippingPayload`
      // dựng từ mode hiệu lực, không phải mode thô) rồi DÙNG LẠI cho voucher
      // revalidate (`payment_method` bên dưới), `amountCollectedNow`,
      // `effectivePaymentMethod`, `nonCashNow` VÀ `successShipping` xa hơn nữa —
      // KHÔNG để từng chỗ tự đọc `shippingPayload.shipping_mode` (thô) riêng như
      // trước: hai/nhiều bản sao của cùng một phép tính là đúng cách bug tiền của
      // round 2 này lọt qua từ round 1 (round 1 chỉ chuẩn hoá biên HIỂN THỊ, bỏ
      // sót cụm tính tiền này).
      const shippingPayload = buildShippingPayload({
        receiveMethod,
        values: {
          ...values,
          shipping_mode: effectiveShippingMode(
            receiveMethod,
            values.shipping_mode as ShippingModeValue | undefined,
            defaultFulfillmentChoice,
          ),
        },
      });
      const hasShippingForCheckout = shippingPayload.shipping_mode !== undefined;
      // Phần khách đã trả tại quầy của đơn GIAO HÀNG — không phụ thuộc khối tiền,
      // nên tính được TRƯỚC `checkoutMoney` và cắt được vòng phụ thuộc: pipeline
      // cần `prepaidAmount` để ra COD/công nợ, còn `amountCollectedNow` của đơn
      // TẠI QUẦY lại cần `customerPayable` của chính pipeline.
      const prepaidForCheckout = hasShippingForCheckout
        ? Number(shippingPayload.prepaid_amount ?? 0)
        : 0;
      // Fix round 1 F2: voucher revalidate ngay bên dưới GỬI `payment_method` lên
      // BE để `ConditionType.PAYMENT_METHOD` chấm đúng — phải dùng ĐÚNG hình thức
      // thanh toán mà đơn THẬT sẽ ghi (`effectivePaymentMethod`), KHÔNG phải
      // `paymentMethod` thô. Đơn giao hàng trả trước = 0đ ẩn khối chọn PTTT (spec
      // §5.2/D15) và `paymentMethod` state per-tab có thể còn sót 'bank' từ lúc
      // tab đó nhận tại quầy; đơn thật luôn ghi 'cash' trong trường hợp này (xem
      // `nonCashNow`/comment bên dưới) — revalidate bằng 'bank' sẽ chấm SAI hình
      // thức so với đơn BE sắp tạo, khiến voucher ràng buộc PTTT lệch hai bên.
      // Trong nhánh `hasShippingForCheckout`, `amountCollectedNow` (tính ở dưới)
      // LUÔN bằng `prepaidForCheckout` — dùng thẳng `prepaidForCheckout` ở đây để
      // khỏi phải kéo `checkoutMoney` (còn phụ thuộc `effectiveDiscount`, chỉ
      // chốt xong SAU vòng revalidate) lên sớm hơn nó cần.
      const effectivePaymentMethod: PaymentMethod =
        hasShippingForCheckout && prepaidForCheckout === 0 ? 'cash' : paymentMethod;

      // Giỏ hàng có thể đã đổi từ lúc áp voucher. Kiểm lại để số tiền thu
      // khớp với summary.total mà BE ghi vào đơn.
      const appliedVouchers = (values.vouchers as VoucherItem[] | undefined) ?? [];
      let effectiveDiscount = voucherDiscount;
      let effectiveVoucherCodes = appliedVouchers.map((v) => v.code);

      if (appliedVouchers.length > 0) {
        // TCR-1272: giá gửi đi revalidate PHẢI là giá NET (sau chiết khấu
        // dòng) để `voucher_discount` chốt khớp số BE áp lúc tạo đơn —
        // `buildEvaluateItems` tính đúng công thức đó.
        // F3: dựng MỘT LẦN, dùng lại cho mọi voucher trong vòng lặp bên dưới —
        // bản cũ (trước khi tách `buildEvaluateItems`) cũng hoist ra ngoài; gọi
        // lại trong `.map()` chỉ tốn công dựng thêm N-1 mảng giống hệt nhau.
        const evaluateItems = buildEvaluateItems(orderItems);
        let revalidated;
        try {
          revalidated = await Promise.all(
            appliedVouchers.map(async (v) => ({
              code: v.code,
              label: v.label,
              discountAmount: v.discountAmount,
              result: await validateVoucher.mutateAsync({
                code: v.code,
                items: evaluateItems,
                customer_id: values.customer_user_id as string | undefined,
                store_id: selectedStoreId,
                channel: 'pos',
                payment_method: effectivePaymentMethod,
              }),
            })),
          );
        } catch {
          // Mất mạng / 5xx / 429 — thà thu ngân bấm lại còn hơn thu sai tiền.
          message.error('Không kiểm tra lại được voucher. Vui lòng thử lại.');
          return;
        }

        const { kept, dropped } = partitionValidatedVouchers(revalidated);

        if (dropped.length > 0) {
          const next = kept.map((k) => ({ code: k.code, label: k.label, discountAmount: k.discountAmount }));
          form.setFieldsValue({ vouchers: next, voucher_discount: sumVoucherDiscount(next) });
          setOrderItems((prev) => pruneGiftItemsForCodes(prev, dropped.map((d) => d.code)));
          message.warning(
            `Voucher không còn áp dụng được: ${dropped.map((d) => `${d.code} (${d.reason})`).join('; ')}`,
          );
          // Số phải thu vừa đổi — chặn tại đây, khối tiền ở cột phải đã tự cập
          // nhật theo `voucher_discount` mới để thu ngân xem lại rồi bấm lần nữa.
          return; // chặn tạo đơn, để thu ngân xác nhận lại
        }

        effectiveDiscount = sumVoucherDiscount(kept);
        effectiveVoucherCodes = kept.map((k) => k.code);
        if (effectiveDiscount !== voucherDiscount) {
          // Giỏ đổi làm số tiền giảm thay đổi (voucher %, đơn tối thiểu...).
          // Thu ngân đã thấy số cũ trên màn hình — cập nhật rồi chặn để họ
          // xác nhận lại, tránh thu lệch với summary.total mà BE ghi.
          form.setFieldsValue({ vouchers: kept, voucher_discount: effectiveDiscount });
          message.warning('Số tiền giảm của voucher đã thay đổi theo giỏ hàng. Vui lòng kiểm tra lại và bấm thanh toán.');
          return;
        }
      }

      // Một con số duy nhất cho cả payload gửi BE lẫn COD in ra hoá đơn — đọc từ
      // `values` (snapshot lúc bấm "Tạo đơn hàng"), không đọc lại state watch.
      const loyaltyRedeemForOrder = Number(values.loyalty_redeem_value ?? 0) || 0;

      // `shippingPayload`/`hasShippingForCheckout`/`prepaidForCheckout` đã dựng
      // MỘT LẦN ở trên (trước vòng revalidate voucher, vì `effectivePaymentMethod`
      // gửi cho voucher cũng cần chúng) — không dựng lại ở đây.

      // Task 18 — MỘT nguồn số cho TOÀN BỘ phần còn lại của checkout: payload gửi
      // BE, cổng hạn mức COD, hoá đơn thành công. Tính lại `computePosMoney` ở
      // ĐÂY thay vì đọc `money` (state render-time) trực tiếp, vì khối voucher
      // revalidate phía trên có thể đã đổi `effectiveDiscount` so với
      // `voucherDiscount` state — dùng `money` render-time sẽ mang số VOUCHER CŨ.
      // Cùng `buildPosMoneyInput` mà `useMemo` hiển thị dùng, chỉ khác nguồn dữ
      // liệu (values/effectiveDiscount vừa revalidate thay vì watched state).
      //
      // Final review C1: payload tiền TỪNG dựng từ `computeCheckoutAmounts(
      // computePosTotals(...).grandTotal, …)` — công thức CŨ: VAT cộng từng dòng,
      // KHÔNG có phụ thu, voucher trừ SAU VAT. Màn hình và BE thì đọc
      // `money.customerPayable`. Hai số khác nhau trên cùng một đơn ⇒ đơn phi
      // tiền mặt bị BE ném 400 `PAYMENT_SPLIT_MISMATCH` (không tạo được đơn),
      // đơn tiền mặt thì `cash_amount` thiếu phần phụ thu + VAT của nó nên sổ quỹ
      // lệch và tiền thối tính trên số sai. KHÔNG tính tiền bằng bất cứ công thức
      // nào khác trong hàm này nữa.
      // Phụ thu đọc từ `values` (form store, snapshot lúc bấm nút) chứ KHÔNG từ
      // biến watch `surcharge` của render: `values` là thứ `checkoutMoney` tính
      // trên, nên payload gửi BE phải dùng ĐÚNG con số đó. Hai nguồn cho cùng một
      // khoản tiền trong cùng một hàm là đúng cái đã sinh ra C1 — BE tính lại
      // `customerPayable` không có phụ thu rồi ném 400 PAYMENT_SPLIT_MISMATCH
      // trong khi FE đã thu đủ.
      const surchargeForCheckout = Number(values.surcharge_amount ?? 0) || 0;
      const checkoutMoney = computePosMoney(
        buildPosMoneyInput({
          // TCR-1365 — đọc từ `values` (form store, snapshot lúc bấm nút) chứ
          // KHÔNG từ biến watch của render, cùng lý do đã ghi cho
          // `surchargeForCheckout`: payload gửi BE phải tính trên đúng con số
          // mà `checkoutMoney` tính, nếu không đơn lấy tại chi nhánh sẽ gửi
          // `cash_amount` lệch số BE tính và dính guard CASH_AMOUNT_MISMATCH.
          receiveMethod:
            (values.receive_method as 'delivery' | 'pickup' | 'vault' | undefined) ??
            'delivery',
          items: orderItems,
          surcharge: surchargeForCheckout,
          surchargeServiceId: (values.surcharge_service_id as string | undefined) ?? null,
          voucherDiscount: effectiveDiscount + promotionDiscount,
          tradeDiscount,
          loyaltyRedeemValue: loyaltyRedeemForOrder,
          isB2B: userType === UserType.ORGANIZATION,
          shipping: hasShippingForCheckout
            ? {
                mode: shippingPayload.shipping_mode === 'carrier' ? 'carrier' : 'self',
                fee: Number(shippingPayload.shipping_fee ?? 0),
                feePayer: (shippingPayload.shipping_fee_payer as ShippingFeePayer) ?? 'seller',
                prepaidAmount: prepaidForCheckout,
                debtAmount: Number(values.debt_amount ?? 0) || 0,
                serviceId: (values.shipping_service_id as string | undefined) ?? null,
                // Đọc từ `values` (vừa revalidate) chứ không từ biến watch của
                // render — cùng lý do đã ghi cho `surchargeForCheckout` ngay
                // trên: hai nguồn cho cùng một khoản tiền trong cùng một hàm là
                // đúng cái đã sinh ra lỗi PAYMENT_SPLIT_MISMATCH.
                discount: Number(values.shipping_discount ?? 0) || 0,
              }
            : null,
          serviceOptions,
          defaultVatRates: { surcharge: surchargeDefaultVatRate, shipping: shippingDefaultVatRate },
        }),
      );
      const computedOrderTotalForPayment = checkoutMoney.customerPayable;

      // Đơn giao hàng chỉ thu phần trả trước tại quầy; phần còn lại là COD.
      // Gửi thẳng số PHẢI thu cho đơn giao hàng sẽ khiến BE suy ra
      // prepaid_amount = tổng đơn ⇒ cod_amount = 0 (spec D16).
      const amountCollectedNow = hasShippingForCheckout
        ? prepaidForCheckout
        : checkoutMoney.customerPayable;
      // Review finding (Task 25): KHÔNG dùng `Math.min(<split đơn quầy>,
      // amountCollectedNow)` — hai số khác cơ sở. Split của đơn quầy tính trên
      // `customerPayable` (ô split bị ẩn khi có shipping nên `nonCashAmount`
      // state luôn `undefined` → mặc định cả
      // `payable`); `amountCollectedNow` là `prepaid_amount`, có thể lớn
      // hơn `payable` khi người mua trả phí ship (`ShippingPaymentFields.tsx`
      // cho trả trước tới `customerTotal = payable + shippingFee`). Phần vượt
      // `payable` (đúng bằng phí ship) sẽ rơi xuống dòng tiền mặt dù khách
      // chuyển khoản — sai quỹ tiền mặt cuối ca. Đơn giao hàng KHÔNG có ô tách
      // tiền mặt/chuyển khoản (`PaymentMethodFields.tsx` ẩn `NonCashPaymentSection`
      // khi `hasShipping`) nên chỉ `paymentMethod` một mình quyết định toàn bộ
      // `amountCollectedNow` là tiền mặt hay phi tiền mặt — không được suy ra từ
      // split của đơn quầy (cũng tránh luôn rò dữ liệu split cũ từ
      // tab pickup/vault trước khi đổi sang delivery, vì state đó không còn
      // được đọc ở nhánh giao hàng nữa).
      // Spec §5.2 / D15: trả trước = 0 ⇒ khối chọn hình thức thanh toán bị ẨN
      // (`PaymentMethodFields.tsx`, `showPaymentBlock`). Nhưng `paymentMethod`
      // là state PER-TAB và KHÔNG được reset khi đổi sang chế độ giao hàng — nó
      // có thể còn là 'bank' sót lại từ lúc tab đó nhận tại quầy. Gửi nguyên
      // 'bank' thì payload thành đơn phi tiền mặt mà `non_cash_amount = 0` và
      // `cash_amount = 0`: một "đơn chuyển khoản" không có đồng nào, đúng hình
      // dạng mà guard PAYMENT_SPLIT_MISMATCH của BE từng chặn bằng 400. Thu ngân
      // không thể tự sửa vì ô chọn đã bị ẩn khỏi màn hình.
      // `effectivePaymentMethod` đã tính sẵn Ở TRÊN (trước vòng revalidate
      // voucher, dùng công thức tương đương qua `prepaidForCheckout` thay vì
      // `amountCollectedNow` — hai giá trị này LUÔN bằng nhau khi
      // `hasShippingForCheckout`) — không tính lại ở đây.
      // Đơn TẠI QUẦY: tách tiền mặt / phi tiền mặt trên chính `customerPayable`
      // của pipeline. Kẹp trần vì `nonCashAmount` thu ngân gõ trước khi áp
      // voucher (hoặc trước khi sửa phụ thu) có thể lớn hơn số phải trả hiện tại
      // — để lọt thì `cash_amount` âm bị kẹp về 0 và tổng gửi BE vượt
      // `customerPayable`, lại đúng vào guard PAYMENT_SPLIT_MISMATCH.
      const nonCashNow = hasShippingForCheckout
        ? effectivePaymentMethod === 'cash'
          ? 0
          : amountCollectedNow
        : paymentMethod === 'cash'
          ? 0
          : Math.min(nonCashAmount ?? checkoutMoney.customerPayable, checkoutMoney.customerPayable);

      // Gỡ Drawer đã thu hẹp khoảng giữa "đã validate" và "ghi đơn", nhưng CHƯA
      // đóng hẳn: đơn có voucher còn `await validateVoucher.mutateAsync`, và F3
      // quét mã vẫn ăn bàn phím trong lúc chờ. Dòng vừa quét sẽ hiện trên màn hình
      // mà không vào đơn — thu ngân thu tiền theo con số đang thấy, đơn ghi thiếu.
      // ĐỪNG xoá guard này với lý do "không còn Drawer".
      //
      // Đọc `activeRef.current` chứ không phải `orderItems`: `orderItems` là
      // snapshot của closure lúc bấm nút (đúng thứ đã validate và sẽ gửi đi), ref
      // mới là giỏ SỐNG mà màn hình đang hiển thị.
      const liveItems = activeRef.current.orderItems;
      if (cartSignature(liveItems) !== validatedCartSignature) {
        setItemErrors(validatePosItems(liveItems, fulfillmentMode));
        void message.error(
          'Giỏ hàng vừa thay đổi — vui lòng kiểm tra lại đơn rồi bấm "Ghi hoá đơn và thanh toán".',
        );
        return;
      }

      // Chặn COD vượt hạn mức của hãng TRƯỚC khi gọi tạo đơn. Không huỷ đơn,
      // không đụng gì tới giỏ — chỉ dừng lại và nói rõ lý do, thu ngân sửa rồi
      // bấm lại. BE cũng kiểm y hệt (`pos-order.service`): màn hình chặn cho
      // nhanh, còn BE mới là chốt chặn thật vì COD do server tính.
      if (
        hasShippingForCheckout &&
        shippingPayload.shipping_mode === 'carrier' &&
        selectedCarrierCodLimit
      ) {
        // `checkoutMoney.cod` ĐÃ trừ nợ (`computePosMoney` bước 7 tự kẹp debt
        // vào `[0, codDefault]`) — không cần gọi `clampShippingDebt` tay nữa,
        // cổng này giờ đọc thẳng số pipeline đã tính, cùng con số với
        // `ShippingTotals` bên phải và với BE.
        const codError = buildShippingCodLimitError(checkoutMoney.cod, selectedCarrierCodLimit);
        if (codError) {
          void message.error(codError);
          return;
        }
      }

      // Task 9 để lại cổng này CHƯA chặn thật — `InsuranceField` chỉ tô đỏ ô tại
      // chỗ (cosmetic, gate bằng `checkoutAttempted`), không có gì cản
      // `createOrder` bay đi khi thu ngân tick bảo hiểm mà bỏ trống giá trị khai
      // giá. Đóng nốt ở đây, cùng khuôn với cổng hạn mức COD ngay phía trên: chặn
      // trước khi gọi BE, không đụng gì tới giỏ, thu ngân sửa rồi bấm lại.
      if (
        values.shipping_insurance_enabled === true &&
        !(typeof values.shipping_insurance_value === 'number' && values.shipping_insurance_value > 0)
      ) {
        void message.error('Nhập giá trị khai giá hoặc bỏ chọn bảo hiểm');
        return;
      }

      if (activeRef.current.id !== checkoutTabId) {
        message.error('Đơn đang xử lý — tab đã chuyển, không thể ghi đơn từ dữ liệu khác. Vui lòng quay lại tab cũ và thử lại.');
        return;
      }
      const liveTab = activeRef.current;
      const liveCartSignature = cartSignature(liveTab.orderItems);
      const liveInputSignature = checkoutInputSignature({
        formValues: form.getFieldsValue(true) as Record<string, unknown>,
        cartSignature: liveCartSignature,
        fulfillmentMode: liveTab.fulfillmentMode,
        orderType: liveTab.orderType,
        paymentMethod: liveTab.paymentMethod,
        paymentAccountId: liveTab.paymentAccountId,
        nonCashAmount: liveTab.nonCashAmount,
        cashReceived: liveTab.cashReceived,
        selectedSessionId: liveTab.selectedSessionId,
        commissionStaff: liveTab.commissionStaff,
        saveAsDraft: options?.saveAsDraft,
      });
      if (
        liveCartSignature !== initialCartSignature ||
        liveInputSignature !== initialInputSignature
      ) {
        // Cổng này bắt dữ liệu đổi GIỮA lúc validate và lúc gọi API trong CÙNG
        // một cú bấm — lần ghi đầu tiên của tab chưa có snapshot nào, và lúc đó
        // chưa có gì được ghi để mà phải đóng đơn (xem `checkoutLockMessage`).
        message.error(checkoutLockMessage(liveTab.checkoutAttempt ?? existingAttempt));
        return;
      }

      const attemptMoney: PosCheckoutAttempt['money'] = {
        customerPayable: checkoutMoney.customerPayable,
        collectTotal: checkoutMoney.collectTotal,
        cod: checkoutMoney.cod,
        debt: checkoutMoney.debt,
        orderRevenue: checkoutMoney.orderRevenue,
        amountCollectedNow,
        nonCashAmount: nonCashNow,
        paymentMethod: effectivePaymentMethod,
      };
      const currentAttempt = liveTab.checkoutAttempt ?? existingAttempt;
      if (currentAttempt && !sameCheckoutMoney(currentAttempt.money, attemptMoney)) {
        message.error('Số tiền của đơn đã thay đổi sau lần ghi đầu. Vui lòng đóng đơn và tạo lại.');
        return;
      }
      const checkoutAttempt: PosCheckoutAttempt =
        currentAttempt ?? {
          idempotencyKey: `${checkoutTabId}:${uuidv4()}`,
          cartSignature: initialCartSignature,
          inputSignature: initialInputSignature,
          money: attemptMoney,
        };
      patchTab(checkoutTabId, { checkoutAttempt });

      setCheckoutRequestLoading(true);
      const created = await posService.createOrder(
        // Task 7b fix round 1: KHÔNG còn truyền `shippingPayload` đã dựng ở trên
        // (dòng ~769) — `buildCreateOrderPayload` giờ tự dựng lại từ `values` +
        // mode HIỆU LỰC bên trong nó, nên nó luôn đúng kể cả khi thu ngân chưa
        // bấm gì vào thanh PTNH. `shippingPayload` cục bộ ở trên (round 2: giờ
        // CŨNG dựng từ mode hiệu lực, không còn mode thô) vẫn giữ vai trò riêng —
        // tính `amountCollectedNow`/`effectivePaymentMethod`/`nonCashNow` bên
        // trên và `successShipping` bên dưới. Hai nơi tính từ cùng một cặp
        // `effectiveShippingMode(receiveMethod, values.shipping_mode)` nên luôn
        // ra cùng kết quả — không "hai bản sao lệch nhau" dù nằm ở hai hàm khác
        // nhau (một hàm thuần tách riêng, một closure tại chỗ).
        buildCreateOrderPayload({
          values,
          orderItems,
          userType,
          receiveMethod,
          effectivePaymentMethod,
          paymentAccountId,
          nonCashAmount: nonCashNow,
          amountCollectedNow,
          cashReceived,
          loyaltyRedeemValue: loyaltyRedeemForOrder,
          voucherCodes: effectiveVoucherCodes,
          voucherSessionId: checkoutTabId,
          promotionSnapshotId,
          idempotencyKey: checkoutAttempt.idempotencyKey,
          saveAsDraft: options?.saveAsDraft,
          tradeDiscount,
          surcharge: surchargeForCheckout,
          fulfillmentMode,
          orderType,
          liveSessionId: selectedSessionId,
          commissionStaff,
          defaultFulfillmentChoice,
        }),
      );

      // Nút "Tạo và Đi đơn": đơn ĐÃ ghi ở lời gọi trên — bước này chỉ đẩy vận
      // đơn sang hãng, KHÔNG rollback đơn nếu hỏng. Thu ngân phải thấy cả hai sự
      // thật tách bạch: đơn vào sổ rồi (luôn báo thành công), và vận đơn có đi
      // được hay không (báo riêng, có nút thử lại) — một cuộc gọi carrier lỗi
      // không được phép làm đơn trông như chưa lưu.
      //
      // TCR-1963: `saveAsDraft` và `dispatchShipping` là hai nút bấm riêng biệt
      // (không bao giờ cùng `true` trong một lần gọi `handleCheckout`), nhưng
      // vẫn khoá tường minh `!options?.saveAsDraft` ở đây — đơn nháp không được
      // phép đẩy vận đơn dù lỡ có ai gọi nhầm cả hai cờ cùng lúc trong tương lai.
      let shippingOrder: CheckoutSuccessResult['shippingOrder'];
      let dispatchError: string | undefined;
      if (options?.dispatchShipping && !options?.saveAsDraft) {
        setDispatching(true);
        try {
          const dispatched = await shippingService.createShippingFromOrder(created.order_id);
          shippingOrder = {
            provider: dispatched.provider,
            trackingNumber: dispatched.tracking_number,
          };
        } catch (err: unknown) {
          dispatchError = getApiError(err)?.message ?? 'Không gửi được sang đơn vị vận chuyển';
        } finally {
          setDispatching(false);
        }
      }

      const serverTotal = typeof created.total === 'number' ? created.total : undefined;
      const promotionReconciliationStatus = created.promotion_reconciliation?.status;
      if (promotionReconciliationStatus && promotionReconciliationStatus !== 'completed') {
        patchTab(checkoutTabId, {
          checkoutAttempt: {
            ...checkoutAttempt,
            reconciliationStatus: promotionReconciliationStatus,
            orderId: created.order_id,
            orderNumber: created.order_number,
            serverTotal,
          },
        });
        const guidance = promotionReconciliationGuidance(
          promotionReconciliationStatus,
          created.order_number ?? created.order_id,
        );
        void message.warning({ content: guidance, duration: 8 });
        return;
      }

      // TCR-1963 — "Lưu đơn nháp": đơn đã ghi ở trạng thái Nháp (BE giữ tồn), KHÔNG
      // in hoá đơn / không mở dialog thanh toán. Đặt SAU khối đối soát khuyến mãi ở
      // trên: nếu BE trả `promotion_reconciliation.status !== 'completed'` cho một
      // đơn nháp, nhánh đó đã `return` trước khi tới đây — đi đúng đường bình
      // thường (giữ `checkoutAttempt`, hiện cảnh báo, KHÔNG đóng tab) thay vì báo
      // thành công giả và đóng tab đè lên một đơn còn cần đối soát thủ công.
      if (options?.saveAsDraft) {
        patchTab(checkoutTabId, { checkoutAttempt: undefined });
        setCheckoutAttempted(false);
        // Đơn nháp cũng đã giữ suất flash sale ở BE — panel phải đọc lại ngay,
        // giống hệt lý do ở nhánh ghi đơn thường bên dưới.
        void queryClient.invalidateQueries({ queryKey: ['pos-promotions', 'flash-sales'] });
        const holdDays = generalConfigQuery.data?.draft_order_hold_days ?? 2;
        void message.success(
          `Đã lưu đơn nháp ${created.order_number ?? ''} — tự huỷ sau ${holdDays} ngày nếu chưa gửi xác nhận`,
        );
        closeTab(checkoutTabId);
        refreshCombos();
        return;
      }

      patchTab(checkoutTabId, { checkoutAttempt: undefined });

      // Review finding (Task 25): dialog thành công trước đây luôn dùng
      // `orderTotalForPayment` (= payable, không gồm phí ship) và `cashReceived`
      // thô của tab — với đơn giao hàng, số đó không khớp với `cash_received`
      // vừa gửi BE ở trên (mirror dòng `cash_received` phía trên), và
      // `cashReceived` có thể là dữ liệu CŨ còn sót từ lúc tab ở receive_method
      // khác (ô "Tiền khách đưa" không hiện khi có shipping nên không có cách
      // nào xoá nó qua UI). In hoá đơn với 2 số liệu này cho đơn giao hàng sẽ
      // sai. Bỏ hẳn "Tiền mặt"/"Thối lại" cho đơn giao hàng — ShippingPaymentFields
      // không có khái niệm "khách đưa dư tiền thối", trả trước là số cố định.
      //
      // Final review F4: `total` từng = `amountCollectedNow` (số ĐÃ THU). Hoá đơn
      // in ra vì thế lấy `subtotal`/`discount` từ dòng hàng nhưng `total` từ số
      // đã thu — đơn 1.000.000 trả trước 200.000 in "Tổng phụ 1.000.000 / Giảm 0
      // / Tổng cộng 200.000", không dòng phí ship, không dòng COD. Đây là tờ
      // giấy trao tay khách. `total` phải là số khách phải trả CHO CẢ ĐƠN
      // (`customerTotal`); phần đã thu / còn thu đi thành dòng riêng qua
      // `shipping` bên dưới.
      const successShipping = hasShippingForCheckout
        ? (() => {
            const feePayer = (shippingPayload.shipping_fee_payer as ShippingFeePayer) ?? 'seller';
            const fee = Number(shippingPayload.shipping_fee ?? 0);
            // `checkoutMoney` (đã dựng ở trên, cùng `computePosMoney` BE dùng
            // khi ghi đơn) — hoá đơn in ra không thể lệch số trên màn hình vì
            // cả hai đọc từ đúng MỘT lần gọi pipeline này. `collectTotal` đã
            // gồm phí ĐVVC người mua trả (bước 7) — KHÔNG cộng `fee` thêm lần
            // nữa ở đây, xem điểm dễ sai #1 của Task 18.
            return {
              fee,
              feePayer,
              customerTotal: serverTotal ?? checkoutMoney.collectTotal,
              prepaid: amountCollectedNow,
              cod: checkoutMoney.cod,
              debt: checkoutMoney.debt,
            };
          })()
        : undefined;
      const orderTotal = successShipping
        ? successShipping.customerTotal
        : serverTotal ?? computedOrderTotalForPayment;
      const successCashReceived = successShipping ? undefined : cashReceived;
      // Đơn vừa ghi đã TRỪ suất flash sale ở BE — panel phải đọc lại ngay, đừng
      // để thu ngân nhìn số suất của lần fetch trước rồi hứa với khách một suất
      // vừa do chính mình bán mất.
      void queryClient.invalidateQueries({ queryKey: ['pos-promotions', 'flash-sales'] });
      setSuccessResult({
        orderCode: created.order_number ?? created.order_id,
        dateISO: new Date().toISOString(),
        total: orderTotal,
        paymentMethod: effectivePaymentMethod,
        cashReceived: successCashReceived,
        change: successCashReceived !== undefined ? Math.max(0, successCashReceived - orderTotal) : undefined,
        customerName: (values.customer_name as string) || undefined,
        items: orderItems,
        shipping: successShipping,
        shippingOrder,
        dispatchError,
      });
      setSuccessTabId(checkoutTabId);
      setSuccessOpen(true);
      // Ghi hoá đơn xong — toast tự ẩn, tab hiện tại được đóng và chuyển sang
      // danh sách đơn POS.
      setCheckoutAttempted(false);
      // Combo chỉ refresh khi `onContinue` đóng tab qua `handleCloseTab`.
    } catch (err: unknown) {
      // BE đã trả lời và từ chối ⇒ không có đơn nào để bảo vệ: gỡ khoá ngay để
      // thu ngân sửa đúng thứ BE vừa chê rồi bấm lại. Không gỡ thì chính khối
      // xử lý combo ngay bên dưới (nó TỰ sửa giỏ hàng) cũng làm chữ ký lệch với
      // snapshot, và mọi lần bấm sau đều rơi vào cổng "chờ đối soát".
      if (proveOrderNotCreated(err)) {
        patchTab(checkoutTabId, { checkoutAttempt: undefined });
      }

      // Combo chết giữa chừng: tách hết cụm về dòng lẻ giá gốc rồi nạp lại định
      // nghĩa combo. Giữ nguyên cụm thì bấm lại vẫn 400 y như cũ.
      //
      // So KHÔNG PHÂN BIỆT HOA THƯỜNG: BE có hai thông điệp combo và chúng viết
      // hoa khác nhau — "Combo không còn hiệu lực…" (C hoa) và "Cụm combo …
      // không khớp cấu hình…" (c thường). Khớp `includes('Combo')` sẽ trượt cái
      // thứ hai.
      //
      // Review round 1, Finding 1: BE bọc lỗi qua `AllExceptionFilter` thành
      // `{ success: false, status, error: { message, code } }` — KHÔNG BAO GIỜ
      // có `response.data.message`. `getApiError` (đã import, đã dùng ở
      // `swapItemSerial` trong file này) là hàm DUY NHẤT trong repo đọc đúng
      // field đó (`response.data.error.message`), kể cả khi BE trả mảng. Đọc
      // thẳng `err.response.data.message` như bản trước thì `beMessageText`
      // luôn rỗng — cả khối xử lý bên dưới thành dead code, combo chết không
      // bao giờ được tách ra.
      // Hết suất flash sale đúng lúc ghi hoá đơn: KHÔNG huỷ giỏ. Hạ đúng dòng
      // hết suất về giá niêm yết rồi mời thu ngân bấm lại — huỷ giỏ ở đây là
      // bắt khách nhập lại từ đầu vì một thay đổi tồn kho ngoài tầm kiểm soát
      // của họ (quầy khác vừa bán mất suất cuối).
      const soldOutItemId = parseFlashSaleSoldOut(err);
      if (soldOutItemId) {
        setOrderItems((prev) =>
          prev.map((item) =>
            item.flash_sale_item_id === soldOutItemId || item.flash_sale_id === soldOutItemId
              ? dropFlashPrice({ ...item, flash_sale_item_id: soldOutItemId })
              : item,
          ),
        );
        void message.warning(
          'Suất flash sale vừa hết. Dòng hàng đã chuyển về giá niêm yết, vui lòng kiểm tra lại rồi bấm thanh toán.',
        );
        return;
      }

      const beMessageText = getApiError(err)?.message ?? '';
      if (/combo/i.test(beMessageText)) {
        const groupIds = [
          ...new Set(orderItems.map((item) => item.comboGroupId).filter(Boolean)),
        ] as string[];
        setOrderItems((prev) =>
          mergeLooseDuplicates(
            groupIds.reduce((acc, groupId) => splitComboGroup(acc, groupId, fulfillmentMode), prev),
            fulfillmentMode,
          ),
        );
        refreshCombos();
      }

      // TCR-2129 — BE tự tính lại độ lệch CK và từ chối khi còn dòng thiếu lý do.
      const reasonMsg = formatMissingReasonError(err);
      if (reasonMsg) {
        void message.error(reasonMsg);
        return;
      }

      // Màn hình giữ nguyên mọi thứ đã nhập: lỗi mạng/BE thì thu ngân bấm lại
      // ngay, không phải nhập lại tiền khách đưa.
      // BE ném guard bằng mã lỗi thô (`PREPAID_EXCEEDS_CUSTOMER_TOTAL`…) nên hiện
      // thẳng `message` sẽ đẩy đúng chuỗi mã đó vào mặt thu ngân. `resolve…` dịch
      // các mã đã biết và giữ nguyên hành vi cũ cho phần còn lại.
      void message.error(resolveCheckoutErrorMessage(err));
    } finally {
      setCheckoutRequestLoading(false);
      setCheckoutLoading(false);
    }
  };

  const shippingMode = Form.useWatch('shipping_mode', form) as ShippingMode | undefined;
  const receiveMethodWatch =
    (Form.useWatch('receive_method', form) as 'delivery' | 'pickup' | 'vault' | undefined) ??
    'delivery';
  // Task 7b fix round 1 — IMPORTANT: biên HIỂN THỊ chưa migrate khi effect chuẩn
  // hoá (đã xoá ở Task 7b) là thứ DUY NHẤT từng đảm bảo `shippingMode` (thô, ở
  // trên) luôn có giá trị cho đơn giao hàng. Bỏ effect đó mà không đổi biên này
  // thì khối "Người trả phí vận chuyển"/"Phí vận chuyển"/"Khách trả trước" biến
  // mất khỏi màn hình cho tới khi thu ngân bấm một nút trên thanh PTNH — trong
  // khi đơn vẫn lên BE với `shipping_mode: 'self'` (xem `buildCreateOrderPayload`).
  // Tên biến CỐ Ý khác `shippingMode` để không ai lỡ dùng lẫn giá trị thô: dùng
  // biến NÀY cho mọi chỗ cần biết "đơn có đang là giao hàng không" HAY "đơn có
  // đang HIỆU LỰC là ĐVVC không".
  //
  // Final review fix (C1, critical) — ĐÍNH CHÍNH đoạn comment cũ ở đây: nó từng
  // nói cột ĐVVC + khối khối lượng gói được PHÉP dùng `shippingMode` (thô) vì
  // "effective mode không đổi gì ở hai chỗ đó, chỉ biến undefined thành 'self',
  // không bao giờ thành 'carrier'". Đúng khi viết (mặc định tenant từng luôn là
  // 'self'), nhưng SAI kể từ khi `pos_default_fulfillment_choice` có thể là
  // 'carrier' (Task 4) — `effectiveShippingMode` LÚC ĐÓ biến `undefined` thành
  // chính `'carrier'`. Hai chỗ đó (điều kiện hiện cột ĐVVC + khối "Thông tin gói
  // hàng") vẫn gate bằng `shippingMode` thô nên tab mới, mặc định ĐVVC, chưa ai
  // bấm thanh PTNH: cổng checkout đã đúng là 'carrier' (xem `missingCarrierFields`
  // bên dưới) nhưng KHÔNG CÓ Ô NÀO để nhập hãng/nơi lấy hàng/khối lượng — bug C1.
  // Fix: cả hai chỗ đó giờ cũng đọc `normalizedShippingMode`, y hệt
  // `OrderInfoPanel`/`PaymentMethodFields`/`ShippingTotals` bên dưới.
  const normalizedShippingMode = effectiveShippingMode(receiveMethodWatch, shippingMode, defaultFulfillmentChoice);
  const shippingFeePayer = (Form.useWatch('shipping_fee_payer', form) as ShippingFeePayer) ?? 'seller';
  const shippingFee = (Form.useWatch('shipping_fee', form) as number) ?? 0;
  const prepaidAmount = (Form.useWatch('prepaid_amount', form) as number) ?? 0;
  const debtAmount = (Form.useWatch('debt_amount', form) as number) ?? 0;
  const shippingPartner = Form.useWatch('shipping_partner', form) as string | undefined;
  // Cùng queryKey với `CarrierList` nên TanStack Query dùng lại cache, không
  // phát sinh thêm request.
  const { carriers: shippingCarriers } = useShippingCarriers();
  /**
   * Hạn mức COD của hãng đang chọn. Chỉ áp cho chế độ giao qua hãng — tự giao
   * thì không có hãng nào đặt luật.
   */
  const selectedCarrierCodLimit = useMemo(
    () => shippingCarriers.find((c) => c.code === shippingPartner)?.codLimit,
    [shippingCarriers, shippingPartner],
  );
  const shippingNote = (Form.useWatch('shipping_note', form) as string) ?? '';
  // `description` do `CustomerInfoFields` ghi vào một Form.Item nằm trong
  // CustomerSidebar (`CustomerDialog`) — dialog khách hàng có thể CHƯA từng mở
  // trong phiên này (Form.Item chưa mount) dù giá trị đã nằm sẵn trong store
  // (tab khôi phục từ sessionStorage, hoặc gõ thẳng vào ô ghi chú mới ở đây).
  // Cùng lớp lỗi với `loyalty_redeem_value` ở trên — thiếu `preserve: true` thì
  // ô ghi chú cột phải hiện rỗng bất kể store đang có gì.
  const customerNote = (Form.useWatch('description', { form, preserve: true }) as string) ?? '';
  const shippingLabel = Form.useWatch('shipping_label', form) as ShippingOrderLabel | undefined;
  const shippingPromoCode = (Form.useWatch('shipping_promo_code', form) as string) ?? '';
  const packageWeight = Form.useWatch('package_weight', form) as number | undefined;
  const packageLength = Form.useWatch('package_length', form) as number | undefined;
  const packageWidth = Form.useWatch('package_width', form) as number | undefined;
  const packageHeight = Form.useWatch('package_height', form) as number | undefined;
  const shippingInsuranceEnabled =
    (Form.useWatch('shipping_insurance_enabled', form) as boolean | undefined) ?? false;
  const shippingInsuranceValue = Form.useWatch('shipping_insurance_value', form) as number | undefined;
  // CHỈ để ép POSPage re-render khi thu ngân sửa địa chỉ người nhận — ba field
  // này do `DeliveryRecipientFields` (con của CustomerSidebar) đăng ký, không
  // component nào ở tầng POSPage watch nên sửa địa chỉ không tự kéo re-render
  // ở đây. `useCarrierFeeEstimates` bên dưới đọc `form.getFieldsValue(true)`
  // tại thời điểm POSPage render — thiếu watch này, sửa ĐỊA CHỈ SAU KHI đã có
  // cân nặng/hãng hợp lệ sẽ không bao giờ kéo lại ước tính (chỉ tình cờ đúng
  // khi có field khác của POSPage đổi theo). Không dùng ba biến này cho gì
  // khác — bản thân watch mới là thứ cần, không phải giá trị.
  Form.useWatch('delivery_city_id', form);
  Form.useWatch('delivery_ward_id', form);
  Form.useWatch('delivery_address', form);
  // Y HỆT lý do trên nhưng cho nhánh fallback của `buildEstimatePayload`
  // (`delivery_* ?? customer_*`): chưa nhập địa chỉ NGƯỜI NHẬN riêng (ship về
  // đúng địa chỉ khách hàng — luồng phổ biến) thì payload hỏi hãng dùng thẳng
  // `customer_city_id`/`customer_ward_id`/`customer_address`. Ba field này do
  // `CustomerInfoFields` (cũng con của CustomerSidebar) đăng ký, POSPage cũng
  // không watch — cùng một lỗ hổng re-render, chỉ khác field nguồn.
  Form.useWatch('customer_city_id', form);
  Form.useWatch('customer_ward_id', form);
  Form.useWatch('customer_address', form);

  // ─── Task 18: MỘT nguồn số duy nhất cho cả cột phải lẫn checkout ───────────
  //
  // `OrderInfoPanel`, `ShippingTotals`, `PaymentMethodFields`, hiệu ước phí
  // ĐVVC (`useCarrierFeeEstimates`), effect reset công nợ và `handleCheckout`
  // đều đọc từ CHÍNH kết quả `useMemo` này — không còn nơi nào tự tính lại một
  // phần công thức riêng (đó chính là lỗi Task 18 sửa: `OrderInfoPanel` cũ
  // dùng `payable`/`calcShippingMoney`, BE dùng `computePosMoney`, hai bên đã
  // từng trôi lệch số hiển thị khỏi số lưu vào đơn).
  //
  // Gate theo `normalizedShippingMode` (đã chuẩn hoá), KHÔNG phải `shippingMode`
  // thô — xem comment ở khai báo biến `normalizedShippingMode`.
  const moneyInput = useMemo(
    () =>
      (
        buildPosMoneyInput({
          // TCR-1365 — quyết định đơn có được trừ điểm không (`pickup` được,
          // `vault` không). `normalizedShippingMode` bên dưới KHÔNG thay được
          // vai trò này: nó `undefined` cho cả pickup lẫn vault.
          receiveMethod: receiveMethodWatch,
          items: orderItems,
          surcharge,
          surchargeServiceId,
          // Khuyến mãi tự động (TCR-1069) gộp vào cùng ô `voucherDiscount`:
          // với pipeline tiền thì cả hai đều là khoản giảm CẤP ĐƠN, phân bổ về
          // từng dòng y hệt nhau trước khi tính VAT. Bỏ sót nó ở đây thì khối
          // tiền hiện số chưa trừ khuyến mãi, còn BE ghi số đã trừ.
          voucherDiscount: voucherDiscount + promotionDiscount,
          tradeDiscount,
          loyaltyRedeemValue,
          isB2B,
          shipping: normalizedShippingMode
            ? {
                mode: normalizedShippingMode === 'carrier' ? 'carrier' : 'self',
                fee: shippingFee,
                feePayer: shippingFeePayer,
                prepaidAmount,
                debtAmount,
                serviceId: shippingServiceId,
                discount: shippingDiscount,
              }
            : null,
          serviceOptions,
          defaultVatRates: { surcharge: surchargeDefaultVatRate, shipping: shippingDefaultVatRate },
        })
      ),
    [
      orderItems,
      surcharge,
      surchargeServiceId,
      voucherDiscount,
      promotionDiscount,
      tradeDiscount,
      loyaltyRedeemValue,
      isB2B,
      receiveMethodWatch,
      normalizedShippingMode,
      shippingFee,
      shippingFeePayer,
      prepaidAmount,
      debtAmount,
      shippingServiceId,
      // Thiếu dep này thì khối tiền KHÔNG tính lại khi khoản giảm phí ship về
      // từ evaluate (nó về SAU khi thu ngân nhập phí, nên không dep nào khác
      // đổi cùng lúc): màn hiện đúng phí và đúng chip khuyến mãi nhưng số phải
      // thu vẫn cộng nguyên phí — đã bắt được bằng e2e, không phải suy đoán.
      shippingDiscount,
      serviceOptions,
      surchargeDefaultVatRate,
      shippingDefaultVatRate,
    ],
  );

  const money = useMemo(() => computePosMoney(moneyInput), [moneyInput]);

  // TCR-2055 — popup CK tích luỹ dùng CHÍNH pipeline này: trần = tiền hàng còn
  // lại sau voucher + điểm (tính với trade = 0), "Còn thanh toán" = collectTotal
  // thật với trade = a (gồm VAT phân bổ, phí ship, phụ thu).
  const tradeCap = useMemo(
    () =>
      computePosMoney({
        ...moneyInput,
        orderDiscounts: { ...moneyInput.orderDiscounts, trade: 0 },
      }).goodsTaxableAmount,
    [moneyInput],
  );
  // TCR-1326 I2 — trần giảm (bớt dòng hàng / thêm voucher) mà CK tích luỹ đã đặt
  // vẫn cao hơn thì BE trả 400 lúc thanh toán. Tự kẹp lại + báo một lần; chỉ set
  // khi LỚN HƠN HẲN trần nên sau khi kẹp effect không chạy lại.
  useEffect(() => {
    // Giỏ trống (đang nạp / đổi tab) ⇒ trần 0 tạm thời, không được xoá CK đã đặt.
    if (orderItems.length === 0) return;
    const clamped = clampTradeDiscount(tradeDiscount, tradeCap);
    if (clamped === null) return;
    form.setFieldsValue({ trade_discount: clamped });
    void message.warning(
      `Chiết khấu tích luỹ đã giảm còn ${formatVND(clamped)} do tiền hàng còn lại thay đổi`,
    );
  }, [tradeCap, tradeDiscount, form, orderItems.length]);
  const previewTradePayable = useCallback(
    (a: number) =>
      computePosMoney({
        ...moneyInput,
        orderDiscounts: { ...moneyInput.orderDiscounts, trade: a },
      }).collectTotal,
    [moneyInput],
  );

  // TCR-2058 — VAT dưới từng dòng hàng lấy theo VAT SAU phân bổ voucher/KM/điểm
  // (cùng số BE lưu và lên HĐĐT); `goodsLines[].id === item.id`.
  const lineVatById = useMemo(
    () => new Map(money.goodsLines.map((l) => [l.id, l.vatAmount] as const)),
    [money],
  );

  // TCR-2224 — tiền dòng gồm VAT đã làm tròn; Σ = số khách trả.
  const lineTotalById = useMemo(
    () => new Map(money.goodsLines.map((l) => [l.id, l.lineTotal] as const)),
    [money],
  );

  const handleFeePayerChange = useCallback(
    (v: ShippingFeePayer) => form.setFieldsValue({ shipping_fee_payer: v }),
    [form],
  );
  const handlePrepaidChange = useCallback(
    (v: number) => form.setFieldsValue({ prepaid_amount: v }),
    [form],
  );
  const handleSurchargeRowChange = useCallback(
    (v: ServiceFeeValue) =>
      form.setFieldsValue({ surcharge_amount: v.amount, surcharge_service_id: v.serviceId }),
    [form],
  );
  const handleShippingRowChange = useCallback(
    (v: ServiceFeeValue) =>
      form.setFieldsValue({ shipping_fee: v.amount, shipping_service_id: v.serviceId }),
    [form],
  );
  const handleDebtChange = useCallback(
    (v: number) => form.setFieldsValue({ debt_amount: v }),
    [form],
  );

  // Đơn đổi ⇒ số nợ cũ không còn tương ứng với con số nào. Xem docstring
  // `shouldResetDebt`. Không đưa `debtAmount` vào deps — sẽ tự xoá cái vừa gõ.
  //
  // ⚠️ ĐỌC FRESH bằng `form.getFieldValue`, KHÔNG dùng bốn biến `Form.useWatch`
  // (`shippingFee`/`shippingFeePayer`/`prepaidAmount` và bốn nguồn của `payable`
  // — surcharge/voucher/promotion/trade)
  // — cùng cái bẫy đã ghi ở effect "Thông tin gói hàng" bên dưới: effect đổi tab
  // (`useEffect([active.id, ...])`, khai báo TRƯỚC nên chạy TRƯỚC trong cùng
  // commit) ghi THẬT vào Form ngay, nhưng `Form.useWatch` chỉ trả giá trị mới ở
  // LẦN RENDER SAU. Nếu tái vũ trang bằng biến watch thì render kế tiếp sẽ thấy
  // "bốn nguồn vừa đổi" và xoá oan số nợ của tab vừa mở.
  const readDebtResetInputs = useCallback((): DebtResetInputs => {
    const freshSurcharge = (form.getFieldValue('surcharge_amount') as number | undefined) ?? 0;
    const freshVoucher = (form.getFieldValue('voucher_discount') as number | undefined) ?? 0;
    const freshPromotion = (form.getFieldValue('promotion_discount') as number | undefined) ?? 0;
    const freshTrade = (form.getFieldValue('trade_discount') as number | undefined) ?? 0;
    return {
      payable: computePosPayable(orderItems, freshSurcharge, freshVoucher + freshPromotion, freshTrade),
      shippingFee: (form.getFieldValue('shipping_fee') as number | undefined) ?? 0,
      feePayer: (form.getFieldValue('shipping_fee_payer') as ShippingFeePayer | undefined) ?? 'seller',
      prepaidAmount: (form.getFieldValue('prepaid_amount') as number | undefined) ?? 0,
    };
  }, [form, orderItems]);
  const debtResetInputs = useRef<DebtResetInputs | null>(null);
  // Mốc phát hiện đổi tab. `null` ban đầu ⇒ lần chạy đầu tiên (mount) đi vào
  // đúng nhánh "tái vũ trang", không reset gì.
  const debtResetTabIdRef = useRef<string | null>(null);
  useEffect(() => {
    const next = readDebtResetInputs();
    // HAI TRƯỜNG HỢP KHÁC HẲN NHAU — ĐỪNG GỘP LẠI:
    //
    //  1. ĐỔI TAB (`active.id` đổi): bốn nguồn "đổi" chỉ vì đây là MỘT ĐƠN KHÁC,
    //     không phải vì đơn hiện tại vừa bị sửa. Số nợ của tab mới vừa được khôi
    //     phục từ snapshot và vẫn khớp với chính bốn nguồn của tab đó ⇒ TUYỆT
    //     ĐỐI không reset, chỉ nạp lại mốc so sánh rồi thoát. Gộp hai nhánh làm
    //     một (bản trước đây) thì hai tab bất kỳ gần như luôn khác nhau ở bốn
    //     con số này ⇒ mở lại tab nào là mất số nợ của tab đó, thầm lặng: COD
    //     nhảy về số đầy đủ và hãng vận chuyển thu đủ tiền của khách đã thoả
    //     thuận nợ.
    //
    //  2. CÙNG MỘT TAB mà bốn nguồn đổi (thêm hàng, đổi phí ship, đổi người trả
    //     phí, sửa trả trước): số nợ cũ tính trên đơn cũ ⇒ PHẢI reset về 0.
    if (debtResetTabIdRef.current !== active.id) {
      debtResetTabIdRef.current = active.id;
      debtResetInputs.current = next;
      return;
    }
    if (debtResetInputs.current && shouldResetDebt(debtResetInputs.current, next)) {
      debtResetInputs.current = next;
      if (form.getFieldValue('debt_amount')) form.setFieldValue('debt_amount', 0);
    }
  }, [
    active.id,
    // `payable` (biến render-time) đã bị xoá — `readDebtResetInputs` tự tính
    // fresh bằng `computePosPayable` (đọc `form.getFieldValue`), nhưng effect
    // vẫn cần trigger lại khi TIỀN HÀNG đổi. `money.customerPayable` đã gộp
    // đúng các nguồn đó (items/surcharge/voucher/tradeDiscount) nên dùng làm
    // dep thay cho `payable` cũ.
    money.customerPayable,
    shippingFee,
    shippingFeePayer,
    prepaidAmount,
    readDebtResetInputs,
    form,
  ]);

  /**
   * Nơi lấy hàng của CHI NHÁNH đang bán.
   *
   * Lọc theo `store_id` chứ không đưa cả danh sách: thu ngân chi nhánh A không
   * có lý do gì chọn kho của chi nhánh B, và chọn nhầm nghĩa là hãng tới sai chỗ.
   */
  const { data: allPickupLocations, isLoading: pickupLoading } = usePickupLocations();
  // `preserve: true` BẮT BUỘC: field này không có `Form.Item` nào render nó
  // (dropdown nằm trong `CarrierPanel`, không phải Form.Item), nên thiếu cờ đó
  // `useWatch` luôn trả `undefined` dù store đang giữ giá trị — cổng phí sẽ
  // chặn vĩnh viễn. Cùng lớp lỗi đã ghi ở `description` và `loyalty_redeem_value`.
  const shippingPickupLocationId =
    (Form.useWatch('shipping_pickup_location_id', { form, preserve: true }) as
      | string
      | undefined) ?? undefined;

  const pickupLocationsForStore = useMemo(() => {
    const list = allPickupLocations?.data ?? [];
    if (!selectedStoreId) return [];
    return list.filter((location: PickupLocation) => {
      const storeId =
        typeof location.store_id === 'string' ? location.store_id : location.store_id?._id;
      return storeId === selectedStoreId;
    });
  }, [allPickupLocations, selectedStoreId]);

  /**
   * Đúng MỘT lựa chọn thì chọn hộ; từ hai trở lên để trống cho người dùng tự chọn.
   *
   * Cùng luật với `findPickupLocationId` ở màn tạo vận đơn: điền sẵn khi đang mơ
   * hồ thì người dùng thấy ô đã có giá trị, mặc định là đúng và bấm tiếp.
   *
   * Cũng dọn giá trị cũ khi đổi chi nhánh — giữ lại là gửi hãng tới kho của chi
   * nhánh trước đó.
   */
  useEffect(() => {
    if (shippingMode !== 'carrier') return;
    // CHƯA tải xong thì không đụng gì. Danh sách rỗng lúc đang tải trông y hệt
    // "chi nhánh không có nơi lấy hàng", và xử như nhau sẽ XOÁ lựa chọn hợp lệ
    // của đơn vừa mở lại — người dùng thấy ô tự trống đi mà không hiểu vì sao.
    if (pickupLoading || !allPickupLocations) return;
    const ids = pickupLocationsForStore.map((location: PickupLocation) => location._id);
    if (shippingPickupLocationId && !ids.includes(shippingPickupLocationId)) {
      form.setFieldValue('shipping_pickup_location_id', undefined);
      return;
    }
    if (!shippingPickupLocationId && ids.length === 1) {
      form.setFieldValue('shipping_pickup_location_id', ids[0]);
    }
  }, [
    shippingMode,
    pickupLoading,
    allPickupLocations,
    pickupLocationsForStore,
    shippingPickupLocationId,
    form,
  ]);

  const carrierPanelValue: CarrierPanelValue = {
    packageInfo: {
      weight: packageWeight,
      length: packageLength,
      width: packageWidth,
      height: packageHeight,
    },
    note: shippingNote,
    orderLabel: shippingLabel,
    promoCode: shippingPromoCode,
    partner: shippingPartner,
    insurance: { enabled: shippingInsuranceEnabled, amount: shippingInsuranceValue },
    pickupLocationId: shippingPickupLocationId,
  };

  const estimatableProviders = useMemo(
    () =>
      shippingCarriers
        .filter((c) => c.enabled && isEstimatableCarrierCode(c.code))
        .map((c) => c.code as EstimatableCarrierCode),
    [shippingCarriers],
  );

  // Final review fix round 2 (C1 residue): `normalizedShippingMode`, KHÔNG
  // phải `shippingMode` thô. Trước fix, tab mới mặc định ĐVVC nhưng chưa ai
  // bấm thanh PTNH có `shippingMode` thô rỗng ⇒ `providers: []` ⇒ không hãng
  // nào có phí ước tính ⇒ effect ghi `shipping_fee` bên dưới (nơi DUY NHẤT ghi
  // field này) không bao giờ chạy ⇒ `shipping_fee` giữ nguyên 0. Đây KHÔNG chỉ
  // là hiển thị: `computePosMoney` (tcr-erp-shared) cộng phí ship vào
  // `collectTotal`/COD khi `feePayer === 'buyer'`, nên phí kẹt ở 0 làm COD báo
  // cho hãng THIẾU đúng bằng số tiền ship — số tiền THẬT gửi lên BE bị sai,
  // không chỉ một widget trống trên màn hình.
  const { feeByCarrier, refetch: refetchFees } = useCarrierFeeEstimates({
    providers: normalizedShippingMode === 'carrier' ? estimatableProviders : [],
    storeId: selectedStoreId,
    pickupLocationId: shippingPickupLocationId,
    values: form.getFieldsValue(true) as Record<string, unknown>,
    // Phí chưa biết lúc hỏi ⇒ COD hỏi hãng là tiền hàng trừ trả trước. Xem
    // docstring `cod` trong EstimateFeesDto.
    cod: Math.max(0, money.customerPayable - prepaidAmount),
  });

  // NƠI DUY NHẤT ghi `shipping_fee` ở chế độ ĐVVC — ô nhập đã bị thay bằng dòng
  // read-only, nên nếu chỗ này không ghi thì không còn ai ghi. Hãng lỗi ⇒ 0, và
  // dòng cảnh báo ở `OrderInfoPanel` giải thích cho thu ngân.
  //
  // Cùng lý do `normalizedShippingMode` ở effect ước tính phí ngay trên: gate
  // bằng `shippingMode` thô sẽ để `shipping_fee` kẹt ở giá trị cũ (thường là 0
  // trên tab mới) cho tới khi thu ngân bấm thanh PTNH — sai SỐ TIỀN thật gửi
  // BE, không phải chỉ sai hiển thị.
  const selectedFee = shippingPartner ? feeByCarrier.get(shippingPartner) : undefined;
  useEffect(() => {
    if (normalizedShippingMode !== 'carrier') return;
    const fee = selectedFee?.status === 'ok' ? (selectedFee.fee ?? 0) : 0;
    if (form.getFieldValue('shipping_fee') !== fee) {
      form.setFieldValue('shipping_fee', fee);
    }
  }, [normalizedShippingMode, selectedFee?.status, selectedFee?.fee, form]);

  // NƠI DUY NHẤT điền mặc định "Thông tin gói hàng" — chạy đúng MỘT LẦN mỗi
  // đơn (cờ `package_info_initialized` chặn lần sau), để thu ngân xoá sạch cả
  // bốn ô để nhập lại từ đầu không bị effect này điền đè lên.
  const packageInfoInitialized =
    (Form.useWatch('package_info_initialized', form) as boolean | undefined) ?? false;
  useEffect(() => {
    if (orderItems.length === 0) return;
    // ⚠️ ĐỌC FRESH bằng `form.getFieldValue`, KHÔNG dùng biến `shippingMode`/
    // `packageInfoInitialized` (từ `Form.useWatch`) làm điều kiện chặn: effect
    // đổi tab ở trên (`useEffect([active.id, ...])`, khai báo TRƯỚC effect này
    // nên chạy TRƯỚC trong cùng commit) gọi `form.resetFields()` rồi
    // `form.setFieldsValue(active.customerForm)` — ghi THẬT vào Form ngay lập
    // tức. Nhưng `Form.useWatch` chỉ cập nhật giá trị trả về ở LẦN RENDER SAU,
    // nên trong đúng commit đó, hai biến watch ở trên vẫn còn mang giá trị của
    // TAB CŨ. Nếu dùng chúng làm guard: chuyển từ tab A (ĐVVC, giỏ rỗng, cờ
    // chưa bật) sang tab B (ĐVVC, đã có hàng, cờ ĐÃ bật, khối lượng thu ngân
    // tự nhập) — `orderItems.length` đọc đúng tab B (không qua Form nên luôn
    // fresh), còn `shippingMode`/`packageInfoInitialized` vẫn đọc tab A (stale)
    // ⇒ cả ba guard "qua cửa" ⇒ ghi đè oan giá trị vừa khôi phục của tab B.
    // Đọc fresh bằng `getFieldValue` tránh đúng bẫy này. Vẫn giữ hai biến watch
    // trong dependency array để effect còn có cơ hội chạy lại đúng lúc chúng
    // đổi thật (render kế tiếp).
    if (form.getFieldValue('package_info_initialized') === true) return;
    // Final review fix round 2 (C1 residue): đọc `shipping_mode` FRESH qua
    // `getFieldValue` rồi tự chuẩn hoá bằng `effectiveShippingMode` — KHÔNG
    // dùng biến `normalizedShippingMode` (từ `Form.useWatch`) làm guard, vì lý
    // do y hệt đoạn comment ⚠️ ngay trên: nó có thể còn mang giá trị TAB CŨ
    // trong cùng commit đổi tab. Trước fix, guard đọc `shipping_mode` thô chưa
    // qua chuẩn hoá — tab mới mặc định ĐVVC nhưng chưa ai bấm thanh PTNH có
    // `shipping_mode` thô rỗng ⇒ effect không chạy ⇒ `package_length/width/
    // height` giữ `undefined` ⇒ `buildShippingPayload` bỏ hẳn ba field đó khỏi
    // payload — khác với cùng đơn đó nếu thu ngân bấm tay "Giao cho ĐVVC".
    const effectiveMode = effectiveShippingMode(
      form.getFieldValue('receive_method') as 'delivery' | 'pickup' | 'vault' | undefined,
      form.getFieldValue('shipping_mode') as ShippingModeValue | undefined,
      defaultFulfillmentChoice,
    );
    if (effectiveMode !== 'carrier') return;
    form.setFieldsValue({
      package_weight: DEFAULT_PACKAGE_INFO.weight,
      package_length: DEFAULT_PACKAGE_INFO.length,
      package_width: DEFAULT_PACKAGE_INFO.width,
      package_height: DEFAULT_PACKAGE_INFO.height,
      package_info_initialized: true,
    });
  }, [
    shippingMode,
    receiveMethodWatch,
    defaultFulfillmentChoice,
    orderItems.length,
    packageInfoInitialized,
    form,
  ]);

  const handleCarrierPanelChange = (v: CarrierPanelValue) =>
    form.setFieldsValue({
      package_weight: v.packageInfo.weight,
      package_length: v.packageInfo.length,
      package_width: v.packageInfo.width,
      package_height: v.packageInfo.height,
      package_info_initialized: true,
      shipping_note: v.note,
      shipping_label: v.orderLabel,
      shipping_promo_code: v.promoCode,
      shipping_partner: v.partner,
      shipping_insurance_enabled: v.insurance.enabled,
      shipping_insurance_value: v.insurance.amount,
      shipping_pickup_location_id: v.pickupLocationId,
    });

  return (
    <Form
      className="pos-page-form"
      form={form}
      layout="vertical"
      autoComplete="off"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {(() => {
        const tabsBar = (
          <PosTabsBar
            tabs={tabsState.tabs}
            activeId={active.id}
            canAddMore={tabsState.tabs.length < MAX_TABS}
            disabled={tabInteractionsDisabled}
            onSwitch={handleSwitchTab}
            onAdd={handleAddTab}
            onCloseRequest={handleCloseTabRequest}
          />
        );
        // Khi App Fullscreen: render tabs vào portal slot trên Header để
        // tiết kiệm chiều cao (40px) cho khu vực order. Nhánh `tabsBar` (không
        // portal) chỉ tới được ở frame render đầu tiên — trước khi effect ở
        // trên tìm ra `tabsPortalEl` — hoặc khi không có node portal nào khớp
        // (ví dụ nơi gọi không render Header thật, như trong test).
        return posAppFullscreen && tabsPortalEl ? createPortal(tabsBar, tabsPortalEl) : tabsBar;
      })()}
    <div className="pos-page-main" style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* CENTER: Order panel */}
      <div
        className="pos-order-panel"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {/* Top bar: Search | Nhập DS | Giao Ngay/Đặt Trước toggle */}
        <div
          className="pos-top-bar"
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div className="pos-search-slot" style={{ width: '50%', flexShrink: 0 }}>
            <QRSearchBar
              fulfillmentMode={fulfillmentMode}
              onVariantFound={addItem}
              onComboFound={addCombo}
              // TCR-1126 review round 2 — chỉ màn POS lọc theo kênh POS.
              posChannelOnly
            />
          </div>
          {isB2B ? (
            // TCR-2303 — đơn B2B: popup bảng (tồn kho + sửa SL/giá/CK/VAT trước khi thêm).
            <>
              <Button onClick={() => setQuickAddOpen(true)} data-testid="pos-quick-add-open">
                Thêm nhanh
              </Button>
              <QuickAddItemsModal
                open={quickAddOpen}
                onClose={() => setQuickAddOpen(false)}
                fulfillmentMode={fulfillmentMode}
                storeId={selectedStoreId}
                customerId={customerUserId}
                subtitle={quickAddCustomerName ? `Khách: ${quickAddCustomerName}` : undefined}
                discountPolicyEnabled={discountPolicyEnabled}
                hasDiscountPermission={hasPermission('pos.discount')}
                cartItems={orderItems}
                onConfirm={handleQuickAddConfirm}
              />
            </>
          ) : (
            <BulkQrModalButton
              fulfillmentMode={fulfillmentMode}
              onVariantsFound={(variants) => variants.forEach(addItem)}
            />
          )}
          {canSellInstant && canSellPreorder ? (
            <Segmented
              className="segmented-primary"
              value={fulfillmentMode}
              onChange={(v) => handleFulfillmentChange(v as FulfillmentMode)}
              options={[
                { label: 'Giao Ngay', value: 'INSTANT' },
                { label: 'Đặt Trước', value: 'PREORDER' },
              ]}
            />
          ) : canSellPreorder ? (
            <Tag color="processing">Đặt Trước</Tag>
          ) : null}
        </div>

        {comboSuggestion && (
          <ComboSuggestBanner
            suggestion={comboSuggestion}
            onApply={handleApplyCombo}
            onDismiss={dismissCombo}
          />
        )}
        {/* Không có gợi ý NHƯNG giỏ khớp đủ hàng một combo bị luật "không làm
            tăng giá dòng" loại — nói ra lý do thay vì để màn hình im lặng. */}
        {!comboSuggestion && comboBlocked && <ComboBlockedHint blocked={comboBlocked} />}
        {/* Task 6: banner "đơn đủ điều kiện nhận quà" — CTKM Mua X tặng Y. */}
        {pendingGifts.map((f) => (
          <PromotionGiftBanner
            key={f.source_promotion_id}
            freeItem={f}
            pending={giftPending}
            onAdd={(freeItem) => void handleAddPromotionGift(freeItem)}
          />
        ))}
        <OrderItemsTable
          items={orderItems}
          lineVatById={lineVatById}
          lineTotalById={lineTotalById}
          fulfillmentMode={fulfillmentMode}
          storeId={selectedStoreId}
          onUpdateItem={updateItem}
          onRemoveItem={removeItem}
          onSplitItem={splitItem}
          onDropFlash={dropFlashOnItem}
          onRebalanceFlashGroup={handleRebalanceFlashGroup}
          onSwapSerial={swapItemSerial}
          swapping={swapping}
          itemErrors={itemErrors}
          showSerialCol={serialTrackingEnabled}
          showWeightCol={posWeightColumnEnabled}
          isB2B={isB2B}
          discountPolicyEnabled={discountPolicyEnabled}
          currentUserName={user?.name}
          hasDiscountPermission={hasPermission('pos.discount')}
          onSplitCombo={handleSplitCombo}
          onRemoveCombo={handleRemoveCombo}
          onChangeComboCount={(groupId, nextCount) => {
            void handleChangeComboCount(groupId, nextCount);
          }}
          comboMaxCount={comboMaxCountFor}
          comboCountPendingGroupId={comboCountPending}
        />

        {/* TCR-1126 — lưới gợi ý sản phẩm/combo. Dùng chung `usePosCartAdder`
            với ô tìm kiếm nên click thẻ đi đúng một đường thêm giỏ. */}
        <ProductSuggestPanel
          fulfillmentMode={fulfillmentMode}
          storeId={selectedStoreId}
          onVariantFound={addItem}
          onComboFound={addCombo}
          onFlashSaleFound={addFlashSaleItem}
        />

        {/* Final review fix C1: `normalizedShippingMode`, KHÔNG phải
            `shippingMode` thô — xem comment ở khai báo biến. Thô rỗng thì đơn
            giao hàng mặc định ĐVVC (chưa ai bấm thanh PTNH) sẽ không có ô nào
            để nhập khối lượng gói dù cổng checkout đã đòi. */}
        {normalizedShippingMode === 'carrier' && orderItems.length > 0 && (
          <div
            className="pos-package-info-slot"
            style={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', padding: '8px 12px' }}
          >
            <PackageInfoFields
              value={carrierPanelValue.packageInfo}
              onChange={(packageInfo) =>
                handleCarrierPanelChange({ ...carrierPanelValue, packageInfo })
              }
              hasError={checkoutAttempted}
            />
          </div>
        )}

        {/* Thanh phương thức nhận hàng — NƠI DUY NHẤT trên màn POS chọn được
            phương thức nhận hàng. Neo đáy cột hàng: thu ngân quét hàng xong là
            mắt đã ở đây, không phải chạy sang panel bên phải. */}
        <FulfillmentFormSection form={form} defaultFulfillmentChoice={defaultFulfillmentChoice} />
      </div>

      {/* GIỮA: mọi thứ gửi cho ĐVVC — chỉ tồn tại ở chế độ Giao cho ĐVVC.
          340 + 420 = 760px, đúng bằng bề rộng Drawer cũ ở chế độ này, nên không
          lấy thêm chỗ của bảng hàng so với trước. */}
      {/* Final review fix C1: `normalizedShippingMode`, KHÔNG phải
          `shippingMode` thô — cùng lý do với khối "Thông tin gói hàng" ở trên.
          Thô rỗng thì cột ĐVVC (partner/nơi lấy hàng) không hiện dù đơn mặc
          định đang HIỆU LỰC là 'carrier'. */}
      {normalizedShippingMode === 'carrier' && (
        <div
          className="pos-carrier-col"
          style={{
            width: 340,
            flexShrink: 0,
            borderLeft: '1px solid #e0e0e0',
            background: '#fff',
            overflowY: 'auto',
            padding: 12,
          }}
        >
          <CarrierPanel
            value={carrierPanelValue}
            onChange={handleCarrierPanelChange}
            showErrors={checkoutAttempted}
            feeByCarrier={feeByCarrier}
            onReloadFees={refetchFees}
            pickupLocations={pickupLocationsForStore}
            pickupLoading={pickupLoading}
          />
        </div>
      )}

      {/* PHẢI: khách hàng (scroll) + ba khối tiền neo đáy */}
      <aside
        className="pos-customer-payment-aside"
        style={{
          width: 420,
          flexShrink: 0,
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          background: '#fafafa',
        }}
      >
        {/* `minHeight: 168` là sàn của cột khách hàng, KHÔNG phải 0. Cụm tiền bên
            dưới cao theo nội dung; trên màn thấp (~620px trở xuống) nếu ô này
            được phép co về 0 thì hàng "Loại đơn hàng" + ô tìm khách biến mất
            hoàn toàn — thu ngân không chọn được khách. 168px = vừa đủ ba hàng
            đầu (Loại đơn hàng / Chi nhánh / Tìm khách hàng); phần còn lại của
            CustomerSidebar đã có vùng cuộn riêng bên trong. */}
        <div style={{ flex: 1, minHeight: 168, display: 'flex', flexDirection: 'column' }}>
          <CustomerSidebar
            tradeCap={tradeCap}
            previewTradePayable={previewTradePayable}
            // Tiêu đề chuyển xuống `OrderInfoPanel` (kèm số sản phẩm) — thanh
            // trên đỉnh này chỉ tốn một dòng mà đứng xa khối nó gọi tên.
            hideHeader
            ref={sidebarRef}
            form={form}
            orderType={orderType}
            onOrderTypeChange={setOrderType}
            selectedStoreId={selectedStoreId}
            selectedSessionId={selectedSessionId}
            onSessionChange={handleSessionChange}
            cartItems={orderItems}
            // TCR-1069: điều kiện `payment_methods` của CTKM cần biết thu ngân
            // đang chọn gì, nếu không modal báo "chưa đủ điều kiện" nhầm.
            paymentMethod={paymentMethod}
            showNoCustomerAlert={checkoutAttempted}
            hasItems={orderItems.length > 0}
            onClearItems={() => {
              setOrderItems([]);
              // Xoá giỏ = coi như đơn kế tiếp bắt đầu lại — không được giữ "Bỏ
              // qua" của giỏ vừa xoá (xem comment ở `handleAddTab`).
              refreshCombos();
            }}
            fulfillmentMode={fulfillmentMode}
            onGiftItemsChange={(giftItems) => {
              // `isVoucherGiftItem`, KHÔNG `!i.is_gift` thô: `giftItems` ở đây
              // chỉ là kết quả resolve của VOUCHER (`VoucherModal`) — thay
              // TOÀN BỘ dòng `is_gift` bằng nó sẽ xoá luôn quà CTKM đang có
              // trong giỏ (thêm qua banner `PromotionGiftBanner`) mỗi lần thu
              // ngân mở modal Khuyến mãi & Voucher rồi bấm "Đồng ý", kể cả
              // không đổi voucher nào.
              setOrderItems((prev) => [
                ...prev.filter((i) => !isVoucherGiftItem(i)),
                ...giftItems,
              ]);
            }}
            onFreeItemsChange={setPromotionFreeItems}
            onAppliedPromotionIdsChange={setAppliedPromotionIds}
            onDialogOpenChange={setCustomerDialogOpen}
            validationPolicy={customerValidationPolicy}
            onRetryValidationPolicy={() => void generalConfigQuery.refetch()}
            // review round 3 (finding A): CHỈ POS bật đánh giá KM tự động —
            // `Orders/edit.tsx` dựng lại sidebar này với giỏ hàng khác rỗng
            // nhưng `payable` ở đó lấy từ `order.summary.total` đã chốt, không
            // đi qua `promotionDiscount` nào cả. Bật vô điều kiện thì chip
            // hiện ở màn sửa đơn nói một số mà tổng tiền không đổi theo.
            showAutoPromotions
            // TCR-1146: dialog sửa nhanh ghi thẳng vào hồ sơ khách, cùng chính
            // sách với việc đơn POS cập nhật hồ sơ lúc thanh toán (TCR-1071).
            // Setting chưa load / lỗi ⇒ `false`: thà không lưu còn hơn ghi đè
            // hồ sơ khách ở tenant đã cố tình tắt.
            persistCustomerOnSave={
              generalConfigQuery.data?.pos_auto_sync_customer_enabled ?? false
            }
          />
        </div>

        <div
          className="pos-order-info-stack"
          style={{
            // Cụm này ĐƯỢC co (flexShrink 1) chứ không cứng như trước: khi cột
            // phải không đủ cao, thứ phải nhường là chiều cao hiển thị của cụm
            // tiền (nó tự cuộn — xem `.pos-order-info-stack` trong POSPage.css),
            // không phải cột khách hàng ở trên hay nút Ghi hoá đơn ở dưới.
            flexShrink: 1,
            minHeight: 0,
            borderTop: '1px solid #e0e0e0',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            background: '#fff',
          }}
        >
          <OrderInfoPanel
            itemCount={orderItems.length}
            money={money}
            voucherDiscount={voucherDiscount}
            promotionDiscount={promotionDiscount}
            tradeDiscount={tradeDiscount}
            // TCR-1253 (comment Phạm Duy Anh 19/08) — thêm gate `!isB2B`: trước bản
            // sửa này dòng "Điểm tích luỹ quy đổi" chỉ khoá theo `normalizedShippingMode`,
            // không khoá theo `isB2B`, trong khi `buildPosMoneyInput`/`money.goodsTaxableAmount`
            // ĐÃ zero khoản này cho khách B2B (`orderDiscounts.loyalty` — xem
            // `buildPosMoneyInput.ts`) — hiển thị "-X" nhưng tổng tiền không giảm X.
            //
            // TCR-1365 — điều kiện phải khớp CHÍNH XÁC `loyaltyEligible` trong
            // `buildPosMoneyInput.ts`, nếu không hai bên lại lệch pha theo chiều
            // NGƯỢC lại: đơn lấy tại chi nhánh có `normalizedShippingMode`
            // `undefined`, nên gate cũ giấu mất dòng "-X" trong khi pipeline ĐÃ
            // trừ thật — thu ngân thấy tiền hàng tụt mà không có dòng nào giải
            // thích (bắt được bằng ảnh chụp e2e, không phải suy đoán).
            loyaltyRedeemValue={
              (normalizedShippingMode || receiveMethodWatch === 'pickup') && !isB2B
                ? loyaltyRedeemValue
                : 0
            }
            surchargeRow={{
              value: { serviceId: surchargeServiceId, amount: surcharge },
              onChange: handleSurchargeRowChange,
            }}
            shippingRow={{
              value: { serviceId: shippingServiceId, amount: shippingFee },
              onChange: handleShippingRowChange,
            }}
            serviceOptions={serviceOptions}
            serviceOptionsLoading={serviceOptionsLoading}
            defaultVatRates={{ surcharge: surchargeDefaultVatRate, shipping: shippingDefaultVatRate }}
            feePayer={shippingFeePayer}
            onFeePayerChange={handleFeePayerChange}
            freeshipActive={freeshipActive}
            prepaidAmount={prepaidAmount}
            onPrepaidChange={handlePrepaidChange}
            carrierFee={shippingFee}
            // TCR-1084/1086: đơn ĐVVC không cộng/trừ phí vào doanh thu (shop
            // chỉ thu hộ hãng) — POS ẩn dòng phí, phí hãng vẫn xem được ở
            // danh sách đối tác vận chuyển (CarrierList.tsx).
            hideCarrierFeeRow
            // `normalizedShippingMode`, KHÔNG phải `shippingMode` thô — xem
            // comment ở khai báo biến (Task 7b fix round 1).
            shippingMode={normalizedShippingMode}
            selectedCarrierFeeStatus={selectedFee?.status}
          />

          <PaymentMethodFields
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            payable={money.customerPayable}
            cashReceived={cashReceived}
            onCashReceivedChange={setCashReceived}
            paymentAccountId={paymentAccountId}
            onPaymentAccountChange={setPaymentAccountId}
            nonCashAmount={nonCashAmount}
            onNonCashAmountChange={setNonCashAmount}
            // Cùng lý do như `OrderInfoPanel` ở trên: `shippingMode` thô rỗng sẽ
            // khiến khối "Tiền khách đưa/Tiền thừa" của bán-tại-quầy hiện NHẦM
            // trên một đơn giao hàng chưa ai bấm thanh PTNH — chỏi ngay bên cạnh
            // khối "Khách trả trước" của `OrderInfoPanel` ở trên.
            shippingMode={normalizedShippingMode}
          />

          {normalizedShippingMode && (
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
              <ShippingTotals
                cod={money.cod}
                debt={money.debt}
                codDefault={money.codDefault}
                orderRevenue={money.orderRevenue}
                onDebtChange={handleDebtChange}
                codLimit={
                  normalizedShippingMode === 'carrier' ? selectedCarrierCodLimit : undefined
                }
                compact
              />
            </div>
          )}

          {/*
            * Final review fix I2: POS là màn TẠO đơn — field này LUÔN editable ở
            * đây (không có khái niệm `disabled` như `Orders/edit.tsx`, nơi field
            * khoá theo quyền/`isShippingOnlyEdit`), nên `rules` LUÔN required,
            * không cần điều kiện như bên đó. Dùng `careStaffRules` (TCR-2429) để nhãn lỗi
            * khớp chế độ đơn — B2C vẫn ra đúng câu cũ.
            *
            * Trước fix: field này không có `rules` nào — kết hợp với I1 (xoá
            * khách từng xoá luôn `care_staff_id`), POS có thể ghi đơn với field
            * trống mà manager sau đó KHÔNG lưu lại được trên `Orders/edit` (rule
            * required ở đó chặn) — chặn ở TẠO thay vì chỉ chặn ở SỬA mới đúng.
            * Task 7 đã tự điền `care_staff_id` = user đăng nhập cho tab THẬT SỰ
            * mới nên rule này hiếm khi hiện trên thực tế — chỉ đóng khe hở cho
            * tab khôi phục / field bị xoá bằng đường khác.
            */}
          <FloatingField
            name="care_staff_id"
            // TCR-2429 — đơn B2B: đây là người được ghi nhận doanh số, không phải "nhân viên
            // chăm sóc khách" của chi nhánh ⇒ nhãn/câu báo lỗi đổi theo chế độ (B2C y nguyên).
            label={careStaffCopy.label}
            rules={careStaffRequiredRules}
            required
            className="pos-care-staff-field"
            style={{ marginBottom: 0 }}
          >
            <CareStaffAsyncSelect
              storeId={selectedStoreId}
              onChange={(v) => form.setFieldValue('care_staff_id', v)}
              placeholder={careStaffCopy.placeholder}
              // Chỉ đơn B2B mới đổi phạm vi danh sách sang NVKD GT/MT toàn công ty.
              salesChannel={isB2B ? 'b2b' : undefined}
            />
          </FloatingField>

          <CustomerNoteField
            value={customerNote}
            onChange={(v) => form.setFieldsValue({ description: v })}
          />
        </div>

        {unresolvedCheckoutAttempt && (
          <Alert
            type="warning"
            showIcon
            message="Lần ghi hoá đơn trước chưa rõ kết quả"
            // Hai nút nằm TRONG description, không dùng prop `action`: Alert
            // này sống ở cột phải hẹp, `action` đẩy nút ra cột riêng bên phải
            // và bóp phần chữ còn lại thành một dải 3-4 từ mỗi dòng.
            description={
              <>
                <div>
                  Đơn có thể đã vào sổ. Kiểm tra danh sách đơn hàng trước: nếu đơn đã được ghi thì
                  đóng đơn này, nếu chưa thì bỏ khoá để sửa và ghi lại.
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button size="small" onClick={() => navigate('/orders')}>
                    Xem danh sách đơn
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => patchActive({ checkoutAttempt: undefined })}
                  >
                    Bỏ khoá và sửa lại
                  </Button>
                </div>
              </>
            }
            style={{ margin: '0 16px 8px' }}
          />
        )}

        {manualReconciliationAttempt && (
          <Alert
            type="error"
            showIcon
            message="Khuyến mãi cần được đối soát thủ công"
            description="Đơn đã được ghi nhận nhưng không thể tự hoàn tất khuyến mãi. Không tạo lại đơn này."
            action={
              active.checkoutAttempt?.orderId ? (
                <Button
                  size="small"
                  onClick={() => navigate(`/orders/${active.checkoutAttempt?.orderId}`)}
                >
                  Xem đơn hàng
                </Button>
              ) : undefined
            }
            style={{ margin: '0 16px 8px' }}
          />
        )}

        {validationPolicyProblem && (
          <Alert
            type={validationRequirements.isError ? 'error' : 'warning'}
            showIcon
            message={validationPolicyProblem}
            action={
              validationRequirements.isError ? (
                <Button size="small" onClick={() => void generalConfigQuery.refetch()}>
                  Thử lại
                </Button>
              ) : undefined
            }
            style={{ margin: '0 16px 8px' }}
          />
        )}

        <PaymentFooter
          onCheckout={() => void handleCheckout()}
          // Chỉ hiện nút "Tạo và Đi đơn" khi đơn đang ở chế độ ĐVVC — tự vận
          // chuyển/nhận tại quầy không có khái niệm "đẩy vận đơn ngay tại quầy".
          onCheckoutAndDispatch={
            shippingMode === 'carrier' ? () => void handleCheckout({ dispatchShipping: true }) : undefined
          }
          onSaveDraft={() => void handleCheckout({ saveAsDraft: true })}
          dispatching={dispatching}
          // Thiếu field đã resolve vẫn báo khi bấm; policy chưa resolve thì có
          // Alert giải thích và không cho thực hiện hành động có hậu quả.
          checkoutDisabled={
            checkoutLoading || manualReconciliationAttempt || !validationRequirements.isResolved
          }
        />
      </aside>

      <NfcScanOverlay open={false} error={null} isScanning={false} onClose={() => {}} />

      {/* Task 6: quà CTKM trỏ tới product nhiều variant — cùng dialog
          `VoucherModal` dùng cho quà voucher, mở qua `pickGiftVariant`
          bên trong `usePromotionGiftAdd`. */}
      {giftPickerRequest && (
        <GiftVariantPickerDialog
          open
          onClose={() => handleGiftPickerSelect(null)}
          productName={giftPickerRequest.productName}
          requiredQty={giftPickerRequest.requiredQty}
          options={giftPickerRequest.options}
          onSelect={(option) => handleGiftPickerSelect(option)}
        />
      )}

      <CheckoutSuccessDialog
        open={successOpen}
        result={successResult}
        onContinue={() => {
          const completedTabId = successTabId;
          setSuccessOpen(false);
          if (completedTabId) {
            closeTab(completedTabId);
            refreshCombos();
          }
          setSuccessResult(null);
          setSuccessTabId(null);
          navigate('/orders?channel_group=pos&page=1&pageSize=20');
        }}
      />
    </div>
    </Form>
  );
}
